import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuthStore, useSidebarStore } from './lib/store';
import api, { authAPI, inventoryAPI, bomAPI, ecoAPI, workOrderAPI, supplierAPI, purchaseOrderAPI, mrpAPI, costingAPI, qualityAPI, analyticsAPI, auditAPI, signatureAPI, evolveAPI, aiAPI, notificationAPI, exportAPI, readinessAPI, reportsAPI, validationAPI, portalAPI, seedAPI } from './lib/api';
import { Toaster, toast } from 'sonner';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import './App.css';

// ============ HELPERS ============
const STATUS_CLASSES = {
  Draft: 'badge-draft', Pending: 'badge-pending', PendingApproval: 'badge-pending',
  Approved: 'badge-approved', Released: 'badge-released', Rejected: 'badge-rejected',
  Open: 'badge-open', Closed: 'badge-closed', Critical: 'badge-critical',
  InProgress: 'badge-inprogress', Complete: 'badge-complete', Created: 'badge-draft',
  Active: 'badge-released', Blocked: 'badge-rejected', Received: 'badge-complete',
  Passed: 'badge-complete', Failed: 'badge-rejected', Sent: 'badge-approved',
  Converted: 'badge-complete', Stockout: 'badge-rejected', Warning: 'badge-pending',
};
const StatusBadge = ({ status }) => (
  <span data-testid={`status-badge-${status}`} className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${STATUS_CLASSES[status] || 'badge-draft'}`}>{status}</span>
);

const CHART_COLORS = ['hsl(204,90%,45%)', 'hsl(160,60%,40%)', 'hsl(43,90%,55%)', 'hsl(200,45%,60%)', 'hsl(0,72%,55%)', 'hsl(270,60%,55%)'];

const formatCurrency = (v) => `$${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const formatDateTime = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
};

// ============ SHARED COMPONENTS ============
const KPICard = ({ title, value, subtitle, icon, color = 'accent' }) => (
  <div data-testid={`kpi-${title.replace(/\s/g, '-').toLowerCase()}`} className="bg-card border border-border rounded-lg p-4 hover:border-[hsl(var(--accent))] transition-colors">
    <div className="flex items-center justify-between mb-2">
      <span className="text-muted-foreground text-xs uppercase tracking-wider">{title}</span>
      <span className="text-lg">{icon}</span>
    </div>
    <div className="text-2xl font-bold text-foreground">{value}</div>
    {subtitle && <div className="text-xs text-muted-foreground mt-1">{subtitle}</div>}
  </div>
);

const DataTable = ({ columns, data, onRowClick }) => (
  <div className="overflow-x-auto border border-border rounded-lg">
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-secondary/60 border-b border-border">
          {columns.map((col, i) => <th key={i} className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{col.header}</th>)}
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {data.length === 0 ? (
          <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-muted-foreground">No data available</td></tr>
        ) : data.map((row, i) => (
          <tr key={i} className={`hover:bg-secondary/30 transition-colors ${onRowClick ? 'cursor-pointer' : ''}`} onClick={() => onRowClick?.(row)}>
            {columns.map((col, j) => <td key={j} className="px-4 py-3 text-foreground/90">{col.cell ? col.cell(row) : row[col.accessor]}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const PageHeader = ({ title, subtitle, actions }) => (
  <div className="flex items-center justify-between mb-6">
    <div>
      <h1 className="text-2xl font-bold text-foreground">{title}</h1>
      {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
    </div>
    {actions && <div className="flex items-center gap-2">{actions}</div>}
  </div>
);

const SearchInput = ({ value, onChange, placeholder = 'Search...' }) => (
  <input data-testid="search-input" type="text" value={value} onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder} className="bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] w-64" />
);

const Button = ({ children, variant = 'default', size = 'default', className = '', ...props }) => {
  const variants = {
    default: 'bg-[hsl(var(--accent))] text-white hover:bg-[hsl(204,90%,40%)]',
    outline: 'border border-border text-foreground hover:bg-secondary',
    destructive: 'bg-[hsl(var(--destructive))] text-white hover:bg-[hsl(0,72%,40%)]',
    ghost: 'text-foreground hover:bg-secondary',
  };
  const sizes = { sm: 'px-3 py-1.5 text-xs', default: 'px-4 py-2 text-sm', lg: 'px-6 py-3 text-base' };
  return <button className={`rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${variants[variant]} ${sizes[size]} ${className}`} {...props}>{children}</button>;
};

const Modal = ({ open, onClose, title, children, size = 'md' }) => {
  if (!open) return null;
  const sizes = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 animate-fade-in" onClick={onClose}>
      <div className={`bg-card border border-border rounded-lg p-6 ${sizes[size]} w-full mx-4 animate-slide-in`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">{title}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-xl">&times;</button>
        </div>
        {children}
      </div>
    </div>
  );
};

const TabBar = ({ tabs, active, onTabChange }) => (
  <div className="flex border-b border-border mb-4">
    {tabs.map((tab) => (
      <button key={tab.id} data-testid={`tab-${tab.id}`} onClick={() => onTabChange(tab.id)}
        className={`px-4 py-2.5 text-sm font-medium transition-colors ${active === tab.id ? 'text-[hsl(var(--accent))] border-b-2 border-[hsl(var(--accent))]' : 'text-muted-foreground hover:text-foreground'}`}>
        {tab.label}
      </button>
    ))}
  </div>
);

// ============ E-SIGNATURE MODAL ============
const ESignatureModal = ({ open, onClose, onSign, entityType, entityId, meaning = 'Approved' }) => {
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [selectedMeaning, setSelectedMeaning] = useState(meaning);
  const [loading, setLoading] = useState(false);

  const handleSign = async () => {
    if (!password) { toast.error('Password is required'); return; }
    setLoading(true);
    try {
      const res = await signatureAPI.sign({ password, meaning: selectedMeaning, reason, entityType, entityId });
      toast.success('Signature captured successfully');
      onSign?.(res.data);
      onClose();
      setPassword(''); setReason('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Signature failed');
    } finally { setLoading(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Electronic Signature Required" size="sm">
      <div className="space-y-4">
        <div className="bg-secondary/60 border border-border rounded-md p-3 text-xs text-muted-foreground">
          Per 21 CFR Part 11, this action requires electronic signature with re-authentication.
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Signature Meaning</label>
          <select data-testid="sig-meaning" value={selectedMeaning} onChange={(e) => setSelectedMeaning(e.target.value)}
            className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground">
            <option value="Approved">Approved</option>
            <option value="Reviewed">Reviewed</option>
            <option value="Released">Released</option>
            <option value="Rejected">Rejected</option>
            <option value="Verified">Verified</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Password (Re-authenticate)</label>
          <input data-testid="sig-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground" placeholder="Enter your password" />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Reason (Optional)</label>
          <textarea data-testid="sig-reason" value={reason} onChange={(e) => setReason(e.target.value)}
            className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground" rows={2} placeholder="Reason for this action..." />
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="sig-submit" onClick={handleSign} disabled={loading}>{loading ? 'Signing...' : 'Sign & Confirm'}</Button>
        </div>
      </div>
    </Modal>
  );
};

// ============ SIDEBAR ============
const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: '📊', path: '/' },
  { id: 'inventory', label: 'Inventory', icon: '📦', path: '/inventory' },
  { id: 'bom', label: 'BOM / DMR', icon: '🔧', path: '/bom' },
  { id: 'eco', label: 'ECOs', icon: '📝', path: '/eco' },
  { id: 'build', label: 'Build Mgmt', icon: '🏭', path: '/work-orders' },
  { id: 'mrp', label: 'MRP', icon: '📈', path: '/mrp' },
  { id: 'procurement', label: 'Procurement', icon: '🛒', path: '/procurement' },
  { id: 'suppliers', label: 'Suppliers', icon: '🤝', path: '/suppliers' },
  { id: 'costing', label: 'Costing', icon: '💰', path: '/costing' },
  { id: 'quality', label: 'Quality', icon: '✅', path: '/quality' },
  { id: 'evolve', label: 'Evolve Interface', icon: '🔗', path: '/evolve' },
  { id: 'audit', label: 'Audit Trail', icon: '📋', path: '/audit' },
  { id: 'compliance', label: 'AI & Compliance', icon: '🛡️', path: '/compliance' },
  { id: 'readiness', label: 'Go-Live', icon: '🚀', path: '/readiness' },
  { id: 'validation', label: 'Validation', icon: '📄', path: '/validation' },
  { id: 'users', label: 'Users', icon: '👥', path: '/users' },
];

const Sidebar = () => {
  const { collapsed, toggleSidebar } = useSidebarStore();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);

  useEffect(() => {
    notificationAPI.list().then(r => setNotifications(r.data)).catch(() => {});
    const interval = setInterval(() => {
      notificationAPI.list().then(r => setNotifications(r.data)).catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const criticalCount = notifications.filter(n => n.severity === 'critical').length;
  const totalCount = notifications.length;

  return (
    <aside data-testid="sidebar" className={`fixed left-0 top-0 h-screen bg-card border-r border-border z-40 transition-all duration-200 flex flex-col ${collapsed ? 'w-16' : 'w-56'}`}>
      <div className="flex items-center justify-between p-4 border-b border-border">
        {!collapsed && <span className="text-sm font-bold tracking-wider text-foreground">PROMAXO <span className="text-[hsl(var(--accent))]">ERP</span></span>}
        <button onClick={toggleSidebar} className="text-muted-foreground hover:text-foreground text-lg">{collapsed ? '▶' : '◀'}</button>
      </div>
      {/* Notification Bell */}
      <div className="px-3 pt-3 pb-1">
        <button data-testid="notification-bell" onClick={() => setShowNotifs(!showNotifs)}
          className="relative flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors">
          <span className="text-base">🔔</span>
          {!collapsed && <span>Notifications</span>}
          {totalCount > 0 && (
            <span className={`absolute ${collapsed ? 'right-1 top-0' : 'right-2 top-1.5'} min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold ${criticalCount > 0 ? 'bg-[hsl(0,72%,45%)] text-white' : 'bg-[hsl(43,90%,55%)] text-black'}`}>
              {totalCount}
            </span>
          )}
        </button>
        {showNotifs && !collapsed && (
          <div className="mt-1 mb-2 max-h-64 overflow-y-auto space-y-1 animate-fade-in">
            {notifications.map(n => (
              <div key={n.id} className={`px-3 py-2 rounded-md text-xs ${n.severity === 'critical' ? 'bg-[hsl(0,72%,45%,0.1)] border-l-2 border-[hsl(0,72%,45%)]' : n.severity === 'warning' ? 'bg-[hsl(43,90%,55%,0.08)] border-l-2 border-[hsl(43,90%,55%)]' : 'bg-secondary/40 border-l-2 border-[hsl(var(--accent))]'}`}>
                <div className="font-medium text-foreground">{n.title}</div>
                <div className="text-muted-foreground mt-0.5">{n.message}</div>
              </div>
            ))}
            {notifications.length === 0 && <div className="text-xs text-muted-foreground text-center py-3">No notifications</div>}
          </div>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto py-2">
        {NAV_ITEMS.map((item) => {
          const active = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
          return (
            <Link key={item.id} to={item.path} data-testid={`nav-${item.id}`}
              className={`flex items-center gap-3 px-4 py-2.5 mx-2 rounded-md text-sm transition-colors ${active ? 'bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'}`}>
              <span className="text-base">{item.icon}</span>
              {!collapsed && <span>{item.label}</span>}
            </Link>
          );
        })}
      </nav>
      <div className="p-3 border-t border-border">
        {!collapsed && user && (
          <div className="mb-2">
            <div className="text-xs font-medium text-foreground truncate">{user.firstName} {user.lastName}</div>
            <div className="text-xs text-muted-foreground truncate">{user.role}</div>
          </div>
        )}
        <button data-testid="logout-btn" onClick={() => { logout(); navigate('/login'); }}
          className="flex items-center gap-2 text-xs text-muted-foreground hover:text-destructive transition-colors w-full">
          <span>🚪</span>{!collapsed && 'Logout'}
        </button>
      </div>
    </aside>
  );
};

// ============ LOGIN ============
const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await authAPI.login({ email, password });
      setAuth(res.data.user, res.data.token);
      toast.success(`Welcome, ${res.data.user.firstName}!`);
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  const quickLogin = async (email) => {
    setLoading(true);
    try {
      const res = await authAPI.login({ email, password: 'Promaxo2026!' });
      setAuth(res.data.user, res.data.token);
      toast.success(`Welcome, ${res.data.user.firstName}!`);
      navigate('/');
    } catch (err) { toast.error('Quick login failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="bg-card border border-border rounded-lg p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-foreground">PROMAXO <span className="text-[hsl(var(--accent))]">ERP</span></h1>
          <p className="text-sm text-muted-foreground mt-2">FDA-Compliant Enterprise Resource Planning</p>
        </div>
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Email</label>
            <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-secondary border border-border rounded-md px-3 py-2.5 text-sm text-foreground" placeholder="user@promaxo.com" />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Password</label>
            <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-secondary border border-border rounded-md px-3 py-2.5 text-sm text-foreground" placeholder="Password" />
          </div>
          <Button data-testid="login-submit" type="submit" className="w-full" disabled={loading}>{loading ? 'Signing in...' : 'Sign In'}</Button>
        </form>
        <div className="mt-6 border-t border-border pt-4">
          <p className="text-xs text-muted-foreground mb-3 text-center">Quick Access (Demo)</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: 'Admin', email: 'admin@promaxo.com' },
              { label: 'Engineering', email: 'engineer@promaxo.com' },
              { label: 'Quality', email: 'quality@promaxo.com' },
              { label: 'Procurement', email: 'procurement@promaxo.com' },
              { label: 'Finance', email: 'finance@promaxo.com' },
              { label: 'Operations', email: 'ops@promaxo.com' },
            ].map((u) => (
              <button key={u.email} data-testid={`quick-login-${u.label.toLowerCase()}`} onClick={() => quickLogin(u.email)}
                className="text-xs px-3 py-2 rounded-md border border-border text-muted-foreground hover:text-foreground hover:border-[hsl(var(--accent))] transition-colors">{u.label}</button>
            ))}
          </div>
          <div className="mt-3 text-center">
            <Link to="/portal" className="text-xs text-[hsl(var(--accent))] hover:underline">Supplier Portal Access →</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ DASHBOARD ============
const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    analyticsAPI.dashboard().then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">Loading dashboard...</div>;
  if (!data) return <div className="text-muted-foreground">Failed to load dashboard</div>;

  const kpis = data.kpis;
  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Executive Dashboard" subtitle="Real-time operational overview" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KPICard title="Inventory Items" value={kpis.totalInventoryItems} icon="📦" />
        <KPICard title="Active Work Orders" value={kpis.activeWorkOrders} icon="🏭" subtitle={`${kpis.completedWorkOrders} completed`} />
        <KPICard title="Open POs" value={kpis.openPurchaseOrders} icon="🛒" />
        <KPICard title="Open NCs" value={kpis.openNonconformances} icon="⚠️" subtitle={`${kpis.openCAPAs} open CAPAs`} />
        <KPICard title="Active Suppliers" value={kpis.activeSuppliers} icon="🤝" />
        <KPICard title="Total Build Cost" value={formatCurrency(kpis.totalCost)} icon="💰" />
        <KPICard title="Total Variance" value={formatCurrency(kpis.totalVariance)} icon="📉" />
        <KPICard title="Compliance" value="Active" icon="🛡️" subtitle="21 CFR Part 11" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-4">Build Pipeline</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.buildPipeline}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(223,18%,18%)" />
              <XAxis dataKey="stage" tick={{ fill: 'hsl(215,16%,70%)', fontSize: 11 }} />
              <YAxis tick={{ fill: 'hsl(215,16%,70%)', fontSize: 11 }} />
              <Tooltip contentStyle={{ background: 'hsl(222,30%,9%)', border: '1px solid hsl(223,18%,18%)', borderRadius: '6px' }} />
              <Bar dataKey="count" fill="hsl(204,90%,45%)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-4">Cost Trend</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data.costTrend?.slice().reverse()}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(223,18%,18%)" />
              <XAxis dataKey="date" tickFormatter={(d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''} tick={{ fill: 'hsl(215,16%,70%)', fontSize: 11 }} />
              <YAxis tick={{ fill: 'hsl(215,16%,70%)', fontSize: 11 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ background: 'hsl(222,30%,9%)', border: '1px solid hsl(223,18%,18%)', borderRadius: '6px' }} />
              <Line type="monotone" dataKey="totalCost" stroke="hsl(204,90%,45%)" strokeWidth={2} dot={{ fill: 'hsl(204,90%,45%)', r: 3 }} />
              <Line type="monotone" dataKey="variance" stroke="hsl(43,90%,55%)" strokeWidth={2} dot={{ fill: 'hsl(43,90%,55%)', r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Recent Work Orders</h3>
          {data.recentWorkOrders?.map((wo) => (
            <Link to={`/work-orders/${wo.id}`} key={wo.id} className="flex items-center justify-between py-2 border-b border-border last:border-0 hover:bg-secondary/30 px-2 rounded">
              <div>
                <div className="text-sm text-foreground font-medium">{wo.workOrderNumber}</div>
                <div className="text-xs text-muted-foreground">{wo.productName || 'MRI System'}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{wo.currentStage}</span>
                <StatusBadge status={wo.status} />
              </div>
            </Link>
          ))}
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Stock Alerts</h3>
          {data.stockAlerts?.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No stock alerts</p>
          ) : data.stockAlerts?.map((alert, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
              <div>
                <div className="text-sm text-foreground">{alert.partNumber}</div>
                <div className="text-xs text-muted-foreground">{alert.description}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Qty: {alert.totalOnHand}</span>
                <StatusBadge status={alert.severity} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============ INVENTORY ============
const InventoryList = () => {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  const load = useCallback(() => {
    inventoryAPI.list({ search: search || undefined, limit: 50 }).then(r => { setItems(r.data.items); setTotal(r.data.total); });
  }, [search]);
  useEffect(() => { load(); }, [load]);

  const columns = [
    { header: 'Part Number', cell: (r) => <span className="font-mono text-[hsl(var(--accent))]">{r.partNumber}</span> },
    { header: 'Description', accessor: 'description' },
    { header: 'Category', cell: (r) => <StatusBadge status={r.category} /> },
    { header: 'Type', accessor: 'itemType' },
    { header: 'Std Cost', cell: (r) => formatCurrency(r.standardCost) },
    { header: 'Lead Time', cell: (r) => `${r.leadTimeDays}d` },
    { header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <div data-testid="inventory-page">
      <PageHeader title="Inventory & Materials" subtitle={`${total} items tracked`}
        actions={<><SearchInput value={search} onChange={setSearch} placeholder="Search parts..." /><Button onClick={() => navigate('/inventory/new')}>+ New Item</Button></>} />
      <DataTable columns={columns} data={items} onRowClick={(r) => navigate(`/inventory/${r.id}`)} />
    </div>
  );
};

const InventoryDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('details');
  const [whereUsed, setWhereUsed] = useState([]);
  const [showReceive, setShowReceive] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [locations, setLocations] = useState([]);
  const [receiveForm, setReceiveForm] = useState({ quantity: 1, lotNumber: '', unitCost: 0 });
  const [transferForm, setTransferForm] = useState({ quantity: 1, fromLocationId: '', toLocationId: '', reason: '' });
  const navigate = useNavigate();
  const isNew = id === 'new';
  const [form, setForm] = useState({ partNumber: '', description: '', category: 'Component', itemType: 'Purchased', standardCost: 0, reorderPoint: 0, reorderQty: 0, leadTimeDays: 14 });

  useEffect(() => {
    if (!isNew) {
      inventoryAPI.get(id).then(r => { setData(r.data); setForm(r.data.item); });
      inventoryAPI.whereUsed(id).then(r => setWhereUsed(r.data)).catch(() => {});
    }
    inventoryAPI.locations().then(r => setLocations(r.data)).catch(() => {});
  }, [id, isNew]);

  const handleSave = async () => {
    try {
      if (isNew) { const res = await inventoryAPI.create(form); toast.success('Item created'); navigate(`/inventory/${res.data.id}`); }
      else { await inventoryAPI.update(id, form); toast.success('Item updated'); }
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const handleReceive = async () => {
    try {
      const res = await inventoryAPI.receive({ itemId: id, ...receiveForm });
      toast.success(`Received ${receiveForm.quantity} units (Lot: ${res.data.lot.lotNumber})${res.data.inspectionRequired ? ' - Quarantined for inspection' : ''}`);
      setShowReceive(false);
      setReceiveForm({ quantity: 1, lotNumber: '', unitCost: form.standardCost || 0 });
      inventoryAPI.get(id).then(r => setData(r.data));
    } catch (err) { toast.error(err.response?.data?.detail || 'Receive failed'); }
  };

  const handleTransfer = async () => {
    try {
      await inventoryAPI.transfer({ itemId: id, ...transferForm });
      toast.success(`Transferred ${transferForm.quantity} units`);
      setShowTransfer(false);
      inventoryAPI.get(id).then(r => setData(r.data));
    } catch (err) { toast.error(err.response?.data?.detail || 'Transfer failed'); }
  };

  const handleDisposition = async (lotId, disposition) => {
    try {
      await inventoryAPI.dispositionLot(lotId, { disposition });
      toast.success(`Lot ${disposition.toLowerCase()}ed`);
      inventoryAPI.get(id).then(r => setData(r.data));
    } catch (err) { toast.error(err.response?.data?.detail || 'Disposition failed'); }
  };

  const tabs = [
    { id: 'details', label: 'Details' }, { id: 'stock', label: 'Stock Levels' },
    { id: 'lots', label: 'Lots/Serials' }, { id: 'transactions', label: 'Transactions' },
    { id: 'whereused', label: 'Where Used' },
  ];

  return (
    <div data-testid="inventory-detail-page">
      <PageHeader title={isNew ? 'New Inventory Item' : form.partNumber || 'Item Detail'}
        subtitle={isNew ? 'Create a new tracked item' : form.description}
        actions={<div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/inventory')}>Back</Button>
          {!isNew && <Button variant="outline" onClick={() => setShowReceive(true)}>Receive</Button>}
          {!isNew && <Button variant="outline" onClick={() => setShowTransfer(true)}>Transfer</Button>}
          <Button onClick={handleSave}>Save</Button>
        </div>} />
      {!isNew && <TabBar tabs={tabs} active={tab} onTabChange={setTab} />}

      {/* Receive Modal */}
      <Modal open={showReceive} onClose={() => setShowReceive(false)} title="Receive Inventory" size="sm">
        <div className="space-y-3">
          <div><label className="text-xs text-muted-foreground">Quantity</label>
            <input type="number" value={receiveForm.quantity} onChange={(e) => setReceiveForm(p => ({...p, quantity: Number(e.target.value)}))}
              className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground" /></div>
          <div><label className="text-xs text-muted-foreground">Lot Number (auto-generated if blank)</label>
            <input type="text" value={receiveForm.lotNumber} onChange={(e) => setReceiveForm(p => ({...p, lotNumber: e.target.value}))}
              className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground" placeholder="LOT-YYYY-NNN" /></div>
          <div><label className="text-xs text-muted-foreground">Unit Cost ($)</label>
            <input type="number" step="0.01" value={receiveForm.unitCost} onChange={(e) => setReceiveForm(p => ({...p, unitCost: Number(e.target.value)}))}
              className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground" /></div>
          {form.requiresInspection && <div className="text-xs text-[hsl(43,90%,55%)] bg-[hsl(43,90%,55%,0.08)] p-2 rounded">Items will be quarantined for receiving inspection</div>}
          <div className="flex gap-2 justify-end"><Button variant="outline" onClick={() => setShowReceive(false)}>Cancel</Button><Button onClick={handleReceive}>Receive</Button></div>
        </div>
      </Modal>

      {/* Transfer Modal */}
      <Modal open={showTransfer} onClose={() => setShowTransfer(false)} title="Transfer Inventory" size="sm">
        <div className="space-y-3">
          <div><label className="text-xs text-muted-foreground">From Location</label>
            <select value={transferForm.fromLocationId} onChange={(e) => setTransferForm(p => ({...p, fromLocationId: e.target.value}))}
              className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground">
              <option value="">Select...</option>{locations.map(l => <option key={l.id} value={l.id}>{l.name} ({l.code})</option>)}
            </select></div>
          <div><label className="text-xs text-muted-foreground">To Location</label>
            <select value={transferForm.toLocationId} onChange={(e) => setTransferForm(p => ({...p, toLocationId: e.target.value}))}
              className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground">
              <option value="">Select...</option>{locations.map(l => <option key={l.id} value={l.id}>{l.name} ({l.code})</option>)}
            </select></div>
          <div><label className="text-xs text-muted-foreground">Quantity</label>
            <input type="number" value={transferForm.quantity} onChange={(e) => setTransferForm(p => ({...p, quantity: Number(e.target.value)}))}
              className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground" /></div>
          <div><label className="text-xs text-muted-foreground">Reason</label>
            <input type="text" value={transferForm.reason} onChange={(e) => setTransferForm(p => ({...p, reason: e.target.value}))}
              className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground" placeholder="Transfer reason" /></div>
          <div className="flex gap-2 justify-end"><Button variant="outline" onClick={() => setShowTransfer(false)}>Cancel</Button><Button onClick={handleTransfer}>Transfer</Button></div>
        </div>
      </Modal>

      {(tab === 'details' || isNew) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-card border border-border rounded-lg p-6">
          {[
            { label: 'Part Number', key: 'partNumber', type: 'text' },
            { label: 'Description', key: 'description', type: 'text' },
            { label: 'Category', key: 'category', type: 'select', options: ['Assembly', 'SubAssembly', 'Component', 'RawMaterial', 'Consumable'] },
            { label: 'Item Type', key: 'itemType', type: 'select', options: ['Purchased', 'Manufactured', 'Phantom'] },
            { label: 'Standard Cost ($)', key: 'standardCost', type: 'number' },
            { label: 'Reorder Point', key: 'reorderPoint', type: 'number' },
            { label: 'Reorder Qty', key: 'reorderQty', type: 'number' },
            { label: 'Lead Time (days)', key: 'leadTimeDays', type: 'number' },
          ].map((f) => (
            <div key={f.key}>
              <label className="block text-xs font-medium text-muted-foreground mb-1">{f.label}</label>
              {f.type === 'select' ? (
                <select value={form[f.key] || ''} onChange={(e) => setForm(p => ({...p, [f.key]: e.target.value}))}
                  className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground">
                  {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input type={f.type} value={form[f.key] || ''} onChange={(e) => setForm(p => ({...p, [f.key]: f.type === 'number' ? Number(e.target.value) : e.target.value}))}
                  className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground" />
              )}
            </div>
          ))}
        </div>
      )}
      {tab === 'stock' && data && (
        <DataTable columns={[
          { header: 'Location', cell: (r) => r.locationId },
          { header: 'On Hand', cell: (r) => r.quantityOnHand },
          { header: 'Allocated', cell: (r) => r.quantityAllocated || 0 },
          { header: 'On Order', cell: (r) => r.quantityOnOrder || 0 },
          { header: 'Quarantined', cell: (r) => r.quantityQuarantined || 0 },
        ]} data={data.stockLevels || []} />
      )}
      {tab === 'lots' && data && (
        <DataTable columns={[
          { header: 'Lot Number', accessor: 'lotNumber' },
          { header: 'Serial', accessor: 'serialNumber' },
          { header: 'Qty', accessor: 'quantity' },
          { header: 'Status', cell: (r) => <StatusBadge status={r.status || 'Available'} /> },
          { header: 'Received', cell: (r) => formatDate(r.receivedDate) },
          { header: '', cell: (r) => r.status === 'Quarantined' ? (
            <div className="flex gap-1">
              <button onClick={() => handleDisposition(r.id, 'Accept')} className="text-xs text-[hsl(160,60%,55%)] hover:underline">Accept</button>
              <button onClick={() => handleDisposition(r.id, 'Reject')} className="text-xs text-[hsl(0,72%,55%)] hover:underline">Reject</button>
            </div>
          ) : '' },
        ]} data={data.lotSerials || []} />
      )}
      {tab === 'transactions' && data && (
        <DataTable columns={[
          { header: 'Date', cell: (r) => formatDateTime(r.transactionDate) },
          { header: 'Type', cell: (r) => <StatusBadge status={r.transactionType || ''} /> },
          { header: 'Qty', accessor: 'quantity' },
          { header: 'Reference', accessor: 'referenceType' },
          { header: 'Reason', accessor: 'reason' },
        ]} data={data.transactions || []} />
      )}
      {tab === 'whereused' && (
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3">BOMs using this part ({whereUsed.length})</h3>
          <DataTable columns={[
            { header: 'BOM Number', cell: (r) => <span className="font-mono text-[hsl(var(--accent))]">{r.bomNumber}</span> },
            { header: 'Revision', cell: (r) => r.revision },
            { header: 'Description', accessor: 'description' },
            { header: 'Qty Per', cell: (r) => r.quantity },
            { header: 'Ref Des', accessor: 'referenceDesignator' },
            { header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
          ]} data={whereUsed} />
        </div>
      )}
    </div>
  );
};

// ============ BOM ============
const BOMList = () => {
  const [boms, setBoms] = useState([]);
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  const load = useCallback(() => {
    bomAPI.list({ search: search || undefined }).then(r => { setBoms(r.data.items); setTotal(r.data.total); });
  }, [search]);
  useEffect(() => { load(); }, [load]);

  const columns = [
    { header: 'BOM Number', cell: (r) => <span className="font-mono text-[hsl(var(--accent))]">{r.bomNumber}</span> },
    { header: 'Revision', cell: (r) => <span className="font-mono">{r.revision}</span> },
    { header: 'Description', accessor: 'description' },
    { header: 'DMR Ref', accessor: 'dmrDocumentRef' },
    { header: 'Effective', cell: (r) => formatDate(r.effectiveDate) },
    { header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <div data-testid="bom-page">
      <PageHeader title="Bills of Materials / DMR" subtitle={`${total} BOMs`}
        actions={<><SearchInput value={search} onChange={setSearch} /><Button onClick={() => navigate('/bom/new')}>+ New BOM</Button></>} />
      <DataTable columns={columns} data={boms} onRowClick={(r) => navigate(`/bom/${r.id}`)} />
    </div>
  );
};

const BOMDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [sigOpen, setSigOpen] = useState(false);
  const [sigAction, setSigAction] = useState('');
  const [showAddLine, setShowAddLine] = useState(false);
  const [items, setItems] = useState([]);
  const [newLine, setNewLine] = useState({ itemId: '', quantity: 1, referenceDesignator: '' });
  const navigate = useNavigate();
  const isNew = id === 'new';
  const [form, setForm] = useState({ bomNumber: '', revision: 'A', description: '', dmrDocumentRef: '', dmrRevision: '' });

  const load = () => { if (!isNew) bomAPI.get(id).then(r => { setData(r.data); setForm(r.data.bom); }); };
  useEffect(() => { load(); }, [id, isNew]);
  useEffect(() => { inventoryAPI.list({ limit: 200 }).then(r => setItems(r.data.items)); }, []);

  const handleSave = async () => {
    try {
      if (isNew) {
        const res = await bomAPI.create(form);
        toast.success('BOM created');
        navigate(`/bom/${res.data.id}`);
      } else {
        await bomAPI.update(id, form);
        toast.success('BOM updated');
        load();
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const handleAction = async (action, sigData) => {
    try {
      if (action === 'approve') await bomAPI.approve(id, { signatureId: sigData?.id });
      else if (action === 'release') await bomAPI.release(id, { signatureId: sigData?.id });
      toast.success(`BOM ${action}d successfully`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || `${action} failed`); }
  };

  const handleAddLine = async () => {
    try {
      await bomAPI.addLine(id, newLine);
      toast.success('Line added');
      setNewLine({ itemId: '', quantity: 1, referenceDesignator: '' });
      setShowAddLine(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add line'); }
  };

  const handleRemoveLine = async (lineId) => {
    try {
      await bomAPI.removeLine(id, lineId);
      toast.success('Line removed');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove line'); }
  };

  return (
    <div data-testid="bom-detail-page">
      <PageHeader title={isNew ? 'New BOM' : `${form.bomNumber} Rev ${form.revision}`} subtitle={form.description}
        actions={<div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/bom')}>Back</Button>
          {(isNew || data?.bom?.status === 'Draft') && <Button onClick={handleSave}>{isNew ? 'Create BOM' : 'Save'}</Button>}
          {data?.bom?.status === 'Draft' && <Button onClick={() => { setSigAction('approve'); setSigOpen(true); }}>Approve (e-Sign)</Button>}
          {data?.bom?.status === 'Approved' && <Button onClick={() => { setSigAction('release'); setSigOpen(true); }}>Release (e-Sign)</Button>}
        </div>} />

      {/* BOM Header Form */}
      <div className="bg-card border border-border rounded-lg p-6 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[
            { label: 'BOM Number', key: 'bomNumber', disabled: !isNew },
            { label: 'Revision', key: 'revision', disabled: !isNew },
            { label: 'Description', key: 'description' },
            { label: 'DMR Document Ref', key: 'dmrDocumentRef' },
            { label: 'DMR Revision', key: 'dmrRevision' },
          ].map(f => (
            <div key={f.key}>
              <label className="text-xs text-muted-foreground mb-1 block">{f.label}</label>
              {!isNew && data?.bom?.status !== 'Draft' ? (
                <div className="text-sm text-foreground">{f.key === 'status' ? <StatusBadge status={form[f.key] || 'Draft'} /> : (form[f.key] || '—')}</div>
              ) : (
                <input type="text" value={form[f.key] || ''} onChange={(e) => setForm(p => ({...p, [f.key]: e.target.value}))}
                  disabled={f.disabled && !isNew} className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground disabled:opacity-50" />
              )}
            </div>
          ))}
          {!isNew && <div><label className="text-xs text-muted-foreground">Status</label><div className="mt-1"><StatusBadge status={form.status || 'Draft'} /></div></div>}
        </div>
      </div>

      {/* BOM Lines */}
      {!isNew && data?.lines && (
        <div className="bg-card border border-border rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-foreground">BOM Lines ({data.lines.length} components)</h3>
            {data.bom?.status === 'Draft' && (
              <Button size="sm" onClick={() => setShowAddLine(!showAddLine)}>+ Add Component</Button>
            )}
          </div>
          {showAddLine && (
            <div className="bg-secondary/40 border border-border rounded-md p-3 mb-3 flex items-end gap-3">
              <div className="flex-1">
                <label className="text-xs text-muted-foreground">Component</label>
                <select value={newLine.itemId} onChange={(e) => setNewLine(p => ({...p, itemId: e.target.value}))}
                  className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground">
                  <option value="">Select part...</option>
                  {items.map(i => <option key={i.id} value={i.id}>{i.partNumber} — {i.description}</option>)}
                </select>
              </div>
              <div className="w-24">
                <label className="text-xs text-muted-foreground">Qty</label>
                <input type="number" value={newLine.quantity} onChange={(e) => setNewLine(p => ({...p, quantity: Number(e.target.value)}))}
                  className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground" />
              </div>
              <div className="w-32">
                <label className="text-xs text-muted-foreground">Ref Des</label>
                <input type="text" value={newLine.referenceDesignator} onChange={(e) => setNewLine(p => ({...p, referenceDesignator: e.target.value}))}
                  className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground" />
              </div>
              <Button size="sm" onClick={handleAddLine}>Add</Button>
              <Button size="sm" variant="outline" onClick={() => setShowAddLine(false)}>Cancel</Button>
            </div>
          )}
          <DataTable columns={[
            { header: '#', cell: (r) => r.lineNumber },
            { header: 'Part Number', cell: (r) => <span className="font-mono text-[hsl(var(--accent))]">{r.itemData?.partNumber || r.itemId?.slice(0, 8)}</span> },
            { header: 'Description', cell: (r) => r.itemData?.description || '—' },
            { header: 'Qty', accessor: 'quantity' },
            { header: 'UoM', accessor: 'unitOfMeasure' },
            { header: 'Ref Des', accessor: 'referenceDesignator' },
            ...(data.bom?.status === 'Draft' ? [{
              header: '', cell: (r) => <button onClick={() => handleRemoveLine(r.id)} className="text-xs text-[hsl(0,72%,55%)] hover:underline">Remove</button>
            }] : []),
          ]} data={data.lines} />
        </div>
      )}

      {data?.signatures?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Signatures</h3>
          {data.signatures.map(s => (
            <div key={s.id} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
              <span className="text-xs font-medium text-foreground">{s.userName || s.userEmail}</span>
              <StatusBadge status={s.signatureMeaning} />
              <span className="text-xs text-muted-foreground">{formatDateTime(s.signedAt)}</span>
              {s.reason && <span className="text-xs text-muted-foreground italic">— {s.reason}</span>}
            </div>
          ))}
        </div>
      )}
      <ESignatureModal open={sigOpen} onClose={() => setSigOpen(false)} entityType="bom" entityId={id}
        meaning={sigAction === 'approve' ? 'Approved' : 'Released'} onSign={(sig) => handleAction(sigAction, sig)} />
    </div>
  );
};

// ============ ECO ============
const ECOList = () => {
  const [ecos, setEcos] = useState([]);
  const navigate = useNavigate();
  useEffect(() => { ecoAPI.list({}).then(r => setEcos(r.data.items)); }, []);

  return (
    <div data-testid="eco-page">
      <PageHeader title="Engineering Change Orders" subtitle={`${ecos.length} ECOs`}
        actions={<Button onClick={() => toast.info('ECO creation form coming soon')}>+ New ECO</Button>} />
      <DataTable columns={[
        { header: 'ECO Number', cell: (r) => <span className="font-mono text-[hsl(var(--accent))]">{r.ecoNumber}</span> },
        { header: 'Title', accessor: 'title' },
        { header: 'Priority', cell: (r) => <StatusBadge status={r.priority} /> },
        { header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
        { header: 'Evolve Transfer', cell: (r) => r.transferredToEvolve ? <StatusBadge status="Sent" /> : <StatusBadge status="Pending" /> },
        { header: 'Requested', cell: (r) => formatDate(r.requestedAt) },
      ]} data={ecos} onRowClick={(r) => navigate(`/eco/${r.id}`)} />
    </div>
  );
};

const ECODetail = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { ecoAPI.get(id).then(r => setData(r.data)); }, [id]);
  if (!data) return <div className="text-muted-foreground p-8">Loading...</div>;
  const eco = data.eco;
  return (
    <div data-testid="eco-detail-page">
      <PageHeader title={`${eco.ecoNumber}: ${eco.title}`} subtitle={eco.description}
        actions={<><Button variant="outline" onClick={() => navigate('/eco')}>Back</Button>
          {eco.status === 'Approved' && !eco.transferredToEvolve && <Button onClick={async () => { await ecoAPI.transferToEvolve(id); toast.success('ECO transferred to Evolve'); ecoAPI.get(id).then(r => setData(r.data)); }}>Transfer to Evolve</Button>}</>} />
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div><label className="text-xs text-muted-foreground">Status</label><div className="mt-1"><StatusBadge status={eco.status} /></div></div>
          <div><label className="text-xs text-muted-foreground">Priority</label><div className="mt-1"><StatusBadge status={eco.priority} /></div></div>
          <div><label className="text-xs text-muted-foreground">Reason</label><div className="text-sm text-foreground mt-1">{eco.reason}</div></div>
          <div><label className="text-xs text-muted-foreground">Impact Analysis</label><div className="text-sm text-foreground mt-1">{eco.impactAnalysis || '—'}</div></div>
          <div><label className="text-xs text-muted-foreground">Evolve Transfer</label><div className="mt-1">{eco.transferredToEvolve ? <StatusBadge status="Sent" /> : <StatusBadge status="Pending" />}</div></div>
          <div><label className="text-xs text-muted-foreground">Evolve Ack</label><div className="mt-1">{eco.evolveAckReceived ? <StatusBadge status="Complete" /> : <StatusBadge status="Pending" />}</div></div>
        </div>
      </div>
    </div>
  );
};

// ============ WORK ORDERS ============
const WorkOrderList = () => {
  const [wos, setWos] = useState([]);
  const [total, setTotal] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [boms, setBoms] = useState([]);
  const [newWO, setNewWO] = useState({ workOrderNumber: '', bomId: '', quantity: 1, priority: 'Normal', productName: '' });
  const navigate = useNavigate();

  const load = () => workOrderAPI.list({}).then(r => { setWos(r.data.items); setTotal(r.data.total); });
  useEffect(() => { load(); bomAPI.list({ status: 'Released' }).then(r => setBoms(r.data.items)).catch(() => {}); }, []);

  const handleCreateWO = async () => {
    try {
      const res = await workOrderAPI.create(newWO);
      toast.success('Work Order created');
      setShowCreate(false);
      setNewWO({ workOrderNumber: '', bomId: '', quantity: 1, priority: 'Normal', productName: '' });
      load();
      navigate(`/work-orders/${res.data.id}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const stages = ['Kitting', 'Assembly', 'Testing', 'QC', 'Packaging'];
  const kanban = stages.map(s => ({ stage: s, orders: wos.filter(w => w.currentStage === s && w.status !== 'Complete') }));

  return (
    <div data-testid="work-orders-page">
      <PageHeader title="Build Management" subtitle={`${total} work orders`}
        actions={<Button onClick={() => setShowCreate(true)}>+ New Work Order</Button>} />

      {/* Create WO Modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create Work Order" size="md">
        <div className="space-y-4">
          <div>
            <label className="text-xs text-muted-foreground">WO Number</label>
            <input type="text" value={newWO.workOrderNumber} onChange={(e) => setNewWO(p => ({...p, workOrderNumber: e.target.value}))}
              placeholder="e.g. WO-2026-005" className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">BOM (Released Revision)</label>
            <select value={newWO.bomId} onChange={(e) => {
              const bom = boms.find(b => b.id === e.target.value);
              setNewWO(p => ({...p, bomId: e.target.value, productName: bom?.description || ''}));
            }} className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground">
              <option value="">Select BOM...</option>
              {boms.map(b => <option key={b.id} value={b.id}>{b.bomNumber} Rev {b.revision} — {b.description}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Quantity</label>
              <input type="number" value={newWO.quantity} onChange={(e) => setNewWO(p => ({...p, quantity: Number(e.target.value)}))}
                className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Priority</label>
              <select value={newWO.priority} onChange={(e) => setNewWO(p => ({...p, priority: e.target.value}))}
                className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground">
                <option value="Low">Low</option><option value="Normal">Normal</option><option value="High">High</option><option value="Critical">Critical</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreateWO} disabled={!newWO.workOrderNumber || !newWO.bomId}>Create Work Order</Button>
          </div>
        </div>
      </Modal>

      <div className="grid grid-cols-5 gap-3 mb-6">
        {kanban.map(col => (
          <div key={col.stage} className="bg-card border border-border rounded-lg p-3">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider">{col.stage}</h4>
              <span className="text-xs text-muted-foreground bg-secondary px-2 py-0.5 rounded-full">{col.orders.length}</span>
            </div>
            <div className="space-y-2">
              {col.orders.map(wo => (
                <div key={wo.id} onClick={() => navigate(`/work-orders/${wo.id}`)}
                  className="bg-secondary/60 border border-border rounded-md p-3 cursor-pointer hover:border-[hsl(var(--accent))] transition-colors">
                  <div className="text-xs font-mono text-[hsl(var(--accent))] mb-1">{wo.workOrderNumber}</div>
                  <div className="text-xs text-muted-foreground mb-2">{wo.productName || 'MRI System'}</div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Qty: {wo.quantity}</span>
                    <StatusBadge status={wo.priority} />
                  </div>
                </div>
              ))}
              {col.orders.length === 0 && <div className="text-xs text-muted-foreground text-center py-4">Empty</div>}
            </div>
          </div>
        ))}
      </div>
      <DataTable columns={[
        { header: 'WO Number', cell: (r) => <span className="font-mono text-[hsl(var(--accent))]">{r.workOrderNumber}</span> },
        { header: 'Product', cell: (r) => r.productName || 'MRI System' },
        { header: 'Qty', accessor: 'quantity' },
        { header: 'Stage', cell: (r) => r.currentStage },
        { header: 'Priority', cell: (r) => <StatusBadge status={r.priority} /> },
        { header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
        { header: 'Created', cell: (r) => formatDate(r.createdAt) },
      ]} data={wos} onRowClick={(r) => navigate(`/work-orders/${r.id}`)} />
    </div>
  );
};

const WorkOrderDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [sigOpen, setSigOpen] = useState(false);
  const [selectedGate, setSelectedGate] = useState(null);
  const navigate = useNavigate();

  const load = () => workOrderAPI.get(id).then(r => setData(r.data));
  useEffect(() => { load(); }, [id]);

  if (!data) return <div className="text-muted-foreground p-8">Loading...</div>;
  const wo = data.workOrder;
  const stages = ['Kitting', 'Assembly', 'Testing', 'QC', 'Packaging', 'Complete'];
  const stageIdx = stages.indexOf(wo.currentStage);

  const handlePassGate = async (sig) => {
    if (!selectedGate) return;
    try {
      await workOrderAPI.passGate(id, selectedGate.id, { signatureId: sig?.id });
      toast.success('Inspection gate passed');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="work-order-detail-page">
      <PageHeader title={wo.workOrderNumber} subtitle={`${wo.productName || 'MRI System'} | Qty: ${wo.quantity} | Rev: ${wo.bomRevision}`}
        actions={<div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/work-orders')}>Back</Button>
          {wo.status !== 'Complete' && <Button onClick={async () => { try { await workOrderAPI.advanceStage(id, {}); toast.success('Stage advanced'); load(); } catch(e) { toast.error(e.response?.data?.detail || 'Cannot advance'); } }}>Advance Stage</Button>}
          {wo.status !== 'Complete' && <Button variant="outline" onClick={async () => {
            try { const res = await workOrderAPI.backflush(id); toast.success(res.data.message); load(); }
            catch(e) { toast.error(e.response?.data?.detail || 'Backflush failed'); }
          }}>Backflush Materials</Button>}
        </div>} />

      {/* Stage Progress */}
      <div className="bg-card border border-border rounded-lg p-4 mb-4">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-3">Production Progress</h3>
        <div className="flex items-center gap-1">
          {stages.slice(0, -1).map((stage, i) => (
            <div key={stage} className="flex-1">
              <div className={`h-2 rounded-full ${i < stageIdx ? 'bg-[hsl(160,60%,40%)]' : i === stageIdx ? 'bg-[hsl(204,90%,45%)]' : 'bg-muted'}`} />
              <div className={`text-xs mt-1 text-center ${i === stageIdx ? 'text-[hsl(var(--accent))] font-medium' : 'text-muted-foreground'}`}>{stage}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Work Order Details</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-muted-foreground text-xs">Status</span><div><StatusBadge status={wo.status} /></div></div>
            <div><span className="text-muted-foreground text-xs">Priority</span><div><StatusBadge status={wo.priority} /></div></div>
            <div><span className="text-muted-foreground text-xs">DMR Rev</span><div className="text-foreground">{wo.dmrRevision}</div></div>
            <div><span className="text-muted-foreground text-xs">BOM Rev</span><div className="text-foreground">{wo.bomRevision}</div></div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Cost Summary</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-muted-foreground text-xs">Planned Material</span><div className="text-foreground">{formatCurrency(wo.plannedMaterialCost)}</div></div>
            <div><span className="text-muted-foreground text-xs">Actual Material</span><div className="text-foreground">{formatCurrency(wo.actualMaterialCost)}</div></div>
            <div><span className="text-muted-foreground text-xs">Planned Labor</span><div className="text-foreground">{formatCurrency(wo.plannedLaborCost)}</div></div>
            <div><span className="text-muted-foreground text-xs">Actual Labor</span><div className="text-foreground">{formatCurrency(wo.actualLaborCost)}</div></div>
          </div>
        </div>
      </div>

      {/* Inspection Gates */}
      <div className="bg-card border border-border rounded-lg p-4 mb-4">
        <h3 className="text-sm font-semibold text-foreground mb-3">Inspection Gates</h3>
        <div className="space-y-2">
          {data.inspectionGates?.map(gate => (
            <div key={gate.id} className="flex items-center justify-between py-2 px-3 bg-secondary/30 rounded-md">
              <div className="flex items-center gap-3">
                <span className="text-sm text-foreground">{gate.stageName}</span>
                <StatusBadge status={gate.status} />
              </div>
              {gate.status === 'Pending' && (
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => { setSelectedGate(gate); setSigOpen(true); }}>Pass (e-Sign)</Button>
                  <Button size="sm" variant="destructive" onClick={async () => {
                    try { await workOrderAPI.failGate(id, gate.id, { notes: 'Failed inspection' }); toast.error('Gate failed - NC created'); load(); }
                    catch(e) { toast.error(e.response?.data?.detail || 'Failed'); }
                  }}>Fail</Button>
                </div>
              )}
              {gate.inspectedAt && <span className="text-xs text-muted-foreground">{formatDateTime(gate.inspectedAt)}</span>}
            </div>
          ))}
        </div>
      </div>
      <ESignatureModal open={sigOpen} onClose={() => setSigOpen(false)} entityType="work_order" entityId={id}
        meaning="Approved" onSign={handlePassGate} />
    </div>
  );
};

// ============ MRP ============
const MRPDashboard = () => {
  const [runs, setRuns] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSim, setShowSim] = useState(false);
  const [simParams, setSimParams] = useState({ scenario: 'demand_surge', demandMultiplier: 1.5, leadTimeIncreaseDays: 0 });
  const [simResults, setSimResults] = useState(null);
  const [prDrafts, setPrDrafts] = useState(null);

  useEffect(() => {
    mrpAPI.runs().then(r => setRuns(r.data));
    mrpAPI.suggestions({ status: 'Open' }).then(r => setSuggestions(r.data));
  }, []);

  const runMRP = async () => {
    setLoading(true);
    try {
      const res = await mrpAPI.run({});
      toast.success(`MRP run complete: ${res.data.totalSuggestions} suggestions`);
      mrpAPI.runs().then(r => setRuns(r.data));
      mrpAPI.suggestions({ status: 'Open' }).then(r => setSuggestions(r.data));
    } catch (err) { toast.error('MRP run failed'); }
    finally { setLoading(false); }
  };

  const runSimulation = async () => {
    try {
      const res = await mrpAPI.simulate(simParams);
      setSimResults(res.data);
      toast.success(`Simulation complete: ${res.data.totalSuggestions} suggestions`);
    } catch (err) { toast.error('Simulation failed'); }
  };

  const generatePRDrafts = async () => {
    try {
      const res = await aiAPI.prDrafts({});
      setPrDrafts(res.data);
      toast.success(`${res.data.totalDrafts} PR drafts generated`);
    } catch (err) { toast.error('PR draft generation failed'); }
  };

  return (
    <div data-testid="mrp-page">
      <PageHeader title="Material Requirements Planning" subtitle={`${suggestions.length} open suggestions`}
        actions={<div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowSim(!showSim)}>What-If Simulation</Button>
          <Button variant="outline" onClick={generatePRDrafts}>AI PR Drafts</Button>
          <Button onClick={runMRP} disabled={loading}>{loading ? 'Running...' : 'Run MRP'}</Button>
        </div>} />

      <div className="grid grid-cols-3 gap-4 mb-6">
        <KPICard title="Total Runs" value={runs.length} icon="🔄" />
        <KPICard title="Open Suggestions" value={suggestions.length} icon="📋" />
        <KPICard title="Shortage Alerts" value={suggestions.filter(s => s.reasonCode === 'WO_DEMAND').length} icon="⚠️" />
      </div>

      {/* What-If Simulation Panel */}
      {showSim && (
        <div className="bg-card border border-[hsl(var(--accent))]/30 rounded-lg p-4 mb-4">
          <h3 className="text-sm font-semibold text-[hsl(var(--accent))] mb-3">What-If Simulation (MRP-005)</h3>
          <div className="grid grid-cols-4 gap-3 items-end mb-3">
            <div><label className="text-xs text-muted-foreground">Scenario</label>
              <select value={simParams.scenario} onChange={(e) => setSimParams(p => ({...p, scenario: e.target.value}))}
                className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground">
                <option value="baseline">Baseline</option><option value="demand_surge">Demand Surge</option><option value="supply_disruption">Supply Disruption</option>
              </select></div>
            <div><label className="text-xs text-muted-foreground">Demand Multiplier</label>
              <input type="number" step="0.1" value={simParams.demandMultiplier} onChange={(e) => setSimParams(p => ({...p, demandMultiplier: Number(e.target.value)}))}
                className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground" /></div>
            <div><label className="text-xs text-muted-foreground">Lead Time + Days</label>
              <input type="number" value={simParams.leadTimeIncreaseDays} onChange={(e) => setSimParams(p => ({...p, leadTimeIncreaseDays: Number(e.target.value)}))}
                className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground" /></div>
            <Button onClick={runSimulation}>Run Simulation</Button>
          </div>
          {simResults && (
            <div className="bg-secondary/40 rounded-md p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-mono text-[hsl(var(--accent))]">SIMULATION (not live)</span>
                <span className="text-xs text-muted-foreground">Scenario: {simResults.scenario} | Demand: x{simResults.demandMultiplier} | +{simResults.leadTimeIncrease}d lead time</span>
              </div>
              <div className="text-sm text-foreground mb-2">{simResults.totalSuggestions} suggestions ({simResults.purchaseSuggestions} purchase, {simResults.productionSuggestions} production)</div>
              {simResults.suggestions?.length > 0 && (
                <DataTable columns={[
                  { header: 'Part', cell: (r) => <span className="font-mono text-xs">{r.partNumber}</span> },
                  { header: 'Qty', cell: (r) => r.suggestedQty },
                  { header: 'On Hand', cell: (r) => r.onHand },
                  { header: 'Net Req', cell: (r) => r.netReq?.toFixed(1) },
                  { header: 'Lead Time', cell: (r) => `${r.adjustedLeadTime}d` },
                ]} data={simResults.suggestions.slice(0, 10)} />
              )}
            </div>
          )}
        </div>
      )}

      {/* AI PR Drafts Panel */}
      {prDrafts && (
        <div className="bg-card border border-[hsl(160,60%,40%)]/30 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[hsl(160,60%,55%)]">AI-Generated PR Drafts ({prDrafts.totalDrafts})</h3>
            <span className="text-xs text-muted-foreground">Est. Total: {formatCurrency(prDrafts.totalEstimate)}</span>
          </div>
          <div className="text-xs text-muted-foreground mb-2 bg-secondary/30 p-2 rounded">{prDrafts.note}</div>
          <DataTable columns={[
            { header: 'Part', cell: (r) => <span className="font-mono text-[hsl(var(--accent))]">{r.partNumber}</span> },
            { header: 'Description', accessor: 'description' },
            { header: 'On Hand', cell: (r) => r.onHand },
            { header: 'Reorder', cell: (r) => r.reorderPoint },
            { header: 'Qty', cell: (r) => r.suggestedQty },
            { header: 'Price', cell: (r) => formatCurrency(r.unitPrice) },
            { header: 'Total', cell: (r) => formatCurrency(r.totalEstimate) },
            { header: 'Supplier', cell: (r) => r.suggestedSupplierName },
            { header: 'Urgency', cell: (r) => <StatusBadge status={r.urgency} /> },
          ]} data={prDrafts.drafts} />
        </div>
      )}

      <div className="bg-card border border-border rounded-lg p-4 mb-4">
        <h3 className="text-sm font-semibold text-foreground mb-3">Open Suggestions</h3>
        <DataTable columns={[
          { header: 'Part', cell: (r) => <span className="font-mono text-[hsl(var(--accent))]">{r.itemPartNumber}</span> },
          { header: 'Description', accessor: 'itemDescription' },
          { header: 'Type', cell: (r) => <StatusBadge status={r.suggestionType} /> },
          { header: 'Qty Needed', cell: (r) => r.suggestedQty },
          { header: 'On Hand', cell: (r) => r.currentOnHand },
          { header: 'Reason', cell: (r) => <StatusBadge status={r.reasonCode === 'WO_DEMAND' ? 'Critical' : 'Warning'} /> },
          { header: 'Date', cell: (r) => formatDate(r.suggestedDate) },
          { header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
        ]} data={suggestions} />
      </div>
      <div className="bg-card border border-border rounded-lg p-4">
        <h3 className="text-sm font-semibold text-foreground mb-3">Recent MRP Runs</h3>
        <DataTable columns={[
          { header: 'Run #', cell: (r) => <span className="font-mono">{r.runNumber}</span> },
          { header: 'Type', accessor: 'runType' },
          { header: 'Suggestions', accessor: 'totalSuggestions' },
          { header: 'Purchases', accessor: 'purchaseSuggestions' },
          { header: 'Shortages', accessor: 'shortageAlerts' },
          { header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
          { header: 'Started', cell: (r) => formatDateTime(r.startedAt) },
        ]} data={runs} />
      </div>
    </div>
  );
};

// ============ PROCUREMENT ============
const ProcurementPage = () => {
  const [pos, setPOs] = useState([]);
  const [total, setTotal] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [newPO, setNewPO] = useState({ poNumber: '', supplierId: '', lines: [] });
  const [newLine, setNewLine] = useState({ itemId: '', quantity: 1, unitPrice: 0 });
  const navigate = useNavigate();

  const load = () => purchaseOrderAPI.list({}).then(r => { setPOs(r.data.items); setTotal(r.data.total); });
  useEffect(() => {
    load();
    supplierAPI.list({}).then(r => setSuppliers(r.data.items)).catch(() => {});
    inventoryAPI.list({ limit: 200 }).then(r => setItems(r.data.items)).catch(() => {});
  }, []);

  const addPOLine = () => {
    if (!newLine.itemId) return;
    setNewPO(p => ({...p, lines: [...p.lines, {...newLine, lineNumber: p.lines.length + 1}]}));
    setNewLine({ itemId: '', quantity: 1, unitPrice: 0 });
  };

  const handleCreatePO = async () => {
    try {
      const res = await purchaseOrderAPI.create(newPO);
      toast.success('Purchase Order created');
      setShowCreate(false);
      setNewPO({ poNumber: '', supplierId: '', lines: [] });
      load();
      navigate(`/procurement/${res.data.id}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="procurement-page">
      <PageHeader title="Purchase Orders" subtitle={`${total} orders`}
        actions={<Button onClick={() => setShowCreate(true)}>+ New PO</Button>} />

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create Purchase Order" size="lg">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">PO Number</label>
              <input type="text" value={newPO.poNumber} onChange={(e) => setNewPO(p => ({...p, poNumber: e.target.value}))}
                placeholder="e.g. PO-2026-002" className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Supplier</label>
              <select value={newPO.supplierId} onChange={(e) => setNewPO(p => ({...p, supplierId: e.target.value}))}
                className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground">
                <option value="">Select supplier...</option>
                {suppliers.filter(s => s.status !== 'Blocked').map(s => <option key={s.id} value={s.id}>{s.supplierCode} — {s.name}</option>)}
              </select>
            </div>
          </div>
          <div className="border-t border-border pt-3">
            <h4 className="text-xs font-semibold text-foreground mb-2">Add Line Items</h4>
            <div className="flex gap-2 items-end mb-2">
              <div className="flex-1">
                <select value={newLine.itemId} onChange={(e) => {
                  const item = items.find(i => i.id === e.target.value);
                  setNewLine(p => ({...p, itemId: e.target.value, unitPrice: item?.standardCost || 0}));
                }} className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-xs text-foreground">
                  <option value="">Select item...</option>
                  {items.map(i => <option key={i.id} value={i.id}>{i.partNumber} — {i.description}</option>)}
                </select>
              </div>
              <div className="w-20">
                <input type="number" value={newLine.quantity} onChange={(e) => setNewLine(p => ({...p, quantity: Number(e.target.value)}))}
                  placeholder="Qty" className="w-full bg-secondary border border-border rounded-md px-2 py-2 text-xs text-foreground" />
              </div>
              <div className="w-24">
                <input type="number" value={newLine.unitPrice} onChange={(e) => setNewLine(p => ({...p, unitPrice: Number(e.target.value)}))}
                  placeholder="Price" className="w-full bg-secondary border border-border rounded-md px-2 py-2 text-xs text-foreground" />
              </div>
              <Button size="sm" onClick={addPOLine}>Add</Button>
            </div>
            {newPO.lines.length > 0 && (
              <div className="bg-secondary/40 rounded-md p-2 text-xs space-y-1">
                {newPO.lines.map((l, i) => {
                  const item = items.find(it => it.id === l.itemId);
                  return <div key={i} className="flex justify-between"><span>{item?.partNumber} x{l.quantity}</span><span>{formatCurrency(l.quantity * l.unitPrice)}</span></div>;
                })}
                <div className="border-t border-border pt-1 font-semibold flex justify-between">
                  <span>Total</span><span>{formatCurrency(newPO.lines.reduce((s, l) => s + l.quantity * l.unitPrice, 0))}</span>
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreatePO} disabled={!newPO.poNumber || !newPO.supplierId || newPO.lines.length === 0}>Create PO</Button>
          </div>
        </div>
      </Modal>

      <DataTable columns={[
        { header: 'PO Number', cell: (r) => <span className="font-mono text-[hsl(var(--accent))]">{r.poNumber}</span> },
        { header: 'Supplier', cell: (r) => r.supplierName || r.supplierId?.slice(0, 8) },
        { header: 'Total', cell: (r) => formatCurrency(r.totalAmount) },
        { header: 'Approval', cell: (r) => <StatusBadge status={r.approvalLevel || 'N/A'} /> },
        { header: 'Match', accessor: 'matchStatus' },
        { header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
        { header: 'Date', cell: (r) => formatDate(r.createdAt) },
      ]} data={pos} onRowClick={(r) => navigate(`/procurement/${r.id}`)} />
    </div>
  );
};

const PODetail = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [sigOpen, setSigOpen] = useState(false);
  const navigate = useNavigate();
  const load = () => purchaseOrderAPI.get(id).then(r => setData(r.data));
  useEffect(() => { load(); }, [id]);
  if (!data) return <div className="text-muted-foreground p-8">Loading...</div>;
  const po = data.purchaseOrder;

  return (
    <div data-testid="po-detail-page">
      <PageHeader title={po.poNumber} subtitle={`Supplier: ${data.supplier?.name || ''} | ${formatCurrency(po.totalAmount)}`}
        actions={<div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/procurement')}>Back</Button>
          {(po.status === 'Draft' || po.status === 'PendingApproval') && <Button onClick={() => setSigOpen(true)}>Approve (e-Sign)</Button>}
        </div>} />
      <div className="bg-card border border-border rounded-lg p-6 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div><span className="text-xs text-muted-foreground">Status</span><div><StatusBadge status={po.status} /></div></div>
          <div><span className="text-xs text-muted-foreground">Approval Level</span><div className="text-foreground">{po.approvalLevel}</div></div>
          <div><span className="text-xs text-muted-foreground">Total</span><div className="text-foreground font-semibold">{formatCurrency(po.totalAmount)}</div></div>
          <div><span className="text-xs text-muted-foreground">Payment Terms</span><div className="text-foreground">{po.paymentTerms}</div></div>
        </div>
      </div>
      <div className="bg-card border border-border rounded-lg p-4">
        <h3 className="text-sm font-semibold text-foreground mb-3">PO Lines</h3>
        <DataTable columns={[
          { header: '#', cell: (r) => r.lineNumber },
          { header: 'Item', accessor: 'itemId' },
          { header: 'Quantity', accessor: 'quantity' },
          { header: 'Received', accessor: 'receivedQty' },
          { header: 'Unit Price', cell: (r) => formatCurrency(r.unitPrice) },
          { header: 'Total', cell: (r) => formatCurrency(r.totalPrice) },
        ]} data={data.lines || []} />
      </div>
      <ESignatureModal open={sigOpen} onClose={() => setSigOpen(false)} entityType="purchase_order" entityId={id}
        meaning="Approved" onSign={async (sig) => {
          await purchaseOrderAPI.approve(id, { signatureId: sig.id });
          toast.success('PO approved'); load();
        }} />
    </div>
  );
};

// ============ SUPPLIERS ============
const SupplierList = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const load = useCallback(() => {
    supplierAPI.list({ search: search || undefined }).then(r => setSuppliers(r.data.items));
  }, [search]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="suppliers-page">
      <PageHeader title="Supplier Management" subtitle={`${suppliers.length} suppliers`}
        actions={<SearchInput value={search} onChange={setSearch} placeholder="Search suppliers..." />} />
      <DataTable columns={[
        { header: 'Code', cell: (r) => <span className="font-mono text-[hsl(var(--accent))]">{r.supplierCode}</span> },
        { header: 'Name', accessor: 'name' },
        { header: 'Country', accessor: 'country' },
        { header: 'OTD Rate', cell: (r) => `${r.onTimeDeliveryRate || 0}%` },
        { header: 'Quality', cell: (r) => `${r.qualityRate || 0}%` },
        { header: 'Score', cell: (r) => <span className={`font-semibold ${(r.overallScore || 0) >= 95 ? 'text-[hsl(160,60%,55%)]' : (r.overallScore || 0) >= 90 ? 'text-[hsl(43,90%,55%)]' : 'text-[hsl(0,72%,55%)]'}`}>{r.overallScore || 0}</span> },
        { header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
      ]} data={suppliers} onRowClick={(r) => navigate(`/suppliers/${r.id}`)} />
    </div>
  );
};

const SupplierDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { supplierAPI.get(id).then(r => setData(r.data)); }, [id]);
  if (!data) return <div className="text-muted-foreground p-8">Loading...</div>;
  const sup = data.supplier;

  return (
    <div data-testid="supplier-detail-page">
      <PageHeader title={sup.name} subtitle={`${sup.supplierCode} | ${sup.country || 'N/A'}`}
        actions={<Button variant="outline" onClick={() => navigate('/suppliers')}>Back</Button>} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <KPICard title="On-Time Delivery" value={`${sup.onTimeDeliveryRate}%`} icon="📦" />
        <KPICard title="Quality Rate" value={`${sup.qualityRate}%`} icon="✅" />
        <KPICard title="Overall Score" value={sup.overallScore} icon="⭐" />
      </div>
      <div className="bg-card border border-border rounded-lg p-6 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div><span className="text-xs text-muted-foreground">Status</span><div><StatusBadge status={sup.status} /></div></div>
          <div><span className="text-xs text-muted-foreground">Contact</span><div className="text-foreground">{sup.contactName}</div></div>
          <div><span className="text-xs text-muted-foreground">Email</span><div className="text-foreground">{sup.contactEmail}</div></div>
          <div><span className="text-xs text-muted-foreground">Certifications</span><div className="text-foreground">{(sup.certifications || []).join(', ') || '—'}</div></div>
        </div>
      </div>
      {data.purchaseOrders?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Purchase Orders</h3>
          <DataTable columns={[
            { header: 'PO #', cell: (r) => r.poNumber },
            { header: 'Total', cell: (r) => formatCurrency(r.totalAmount) },
            { header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
            { header: 'Date', cell: (r) => formatDate(r.createdAt) },
          ]} data={data.purchaseOrders} />
        </div>
      )}
    </div>
  );
};

// ============ COSTING ============
const CostingDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState('overview');
  const [stdCosts, setStdCosts] = useState([]);
  const [costAnomaly, setCostAnomaly] = useState(null);
  const [sigOpen, setSigOpen] = useState(false);
  const [selectedSC, setSelectedSC] = useState(null);

  useEffect(() => {
    costingAPI.summary().then(r => setSummary(r.data));
    costingAPI.standardCosts({}).then(r => setStdCosts(r.data)).catch(() => {});
  }, []);

  const runCostAnomaly = async () => {
    try {
      const res = await aiAPI.costAnomaly({});
      setCostAnomaly(res.data);
      toast.success('Cost anomaly analysis complete');
    } catch (err) { toast.error('Analysis failed'); }
  };

  if (!summary) return <div className="text-muted-foreground p-8">Loading...</div>;

  return (
    <div data-testid="costing-page">
      <PageHeader title="Costing & Financial Visibility" subtitle="Real-time cost analysis"
        actions={<div className="flex gap-2">
          <Button variant="outline" onClick={runCostAnomaly}>AI Anomaly Detection</Button>
        </div>} />
      <TabBar tabs={[{ id: 'overview', label: 'Overview' }, { id: 'stdcosts', label: 'Standard Costs' }]} active={tab} onTabChange={setTab} />
      {tab === 'overview' && (<div>
      <div className="grid grid-cols-4 gap-4 mb-6">
        <KPICard title="Total Builds" value={summary.totalBuilds} icon="🏭" />
        <KPICard title="Total Cost" value={formatCurrency(summary.totalCost)} icon="💰" />
        <KPICard title="Total Variance" value={formatCurrency(summary.totalVariance)} icon="📉" />
        <KPICard title="Avg Build Cost" value={formatCurrency(summary.averageBuildCost)} icon="📊" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-4">Cost Breakdown Trend</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={summary.recentRollups?.slice().reverse()}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(223,18%,18%)" />
              <XAxis dataKey="buildDate" tickFormatter={d => d ? new Date(d).toLocaleDateString('en-US', {month:'short', day:'numeric'}) : ''} tick={{ fill: 'hsl(215,16%,70%)', fontSize: 10 }} />
              <YAxis tick={{ fill: 'hsl(215,16%,70%)', fontSize: 10 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ background: 'hsl(222,30%,9%)', border: '1px solid hsl(223,18%,18%)', borderRadius: '6px' }} />
              <Legend />
              <Bar dataKey="materialCost" name="Material" fill="hsl(204,90%,45%)" stackId="a" />
              <Bar dataKey="laborCost" name="Labor" fill="hsl(160,60%,40%)" stackId="a" />
              <Bar dataKey="overheadCost" name="Overhead" fill="hsl(43,90%,55%)" stackId="a" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-4">Variance Trend</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={summary.recentRollups?.slice().reverse()}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(223,18%,18%)" />
              <XAxis dataKey="buildDate" tickFormatter={d => d ? new Date(d).toLocaleDateString('en-US', {month:'short', day:'numeric'}) : ''} tick={{ fill: 'hsl(215,16%,70%)', fontSize: 10 }} />
              <YAxis tick={{ fill: 'hsl(215,16%,70%)', fontSize: 10 }} tickFormatter={v => `$${v.toLocaleString()}`} />
              <Tooltip contentStyle={{ background: 'hsl(222,30%,9%)', border: '1px solid hsl(223,18%,18%)', borderRadius: '6px' }} />
              <Line type="monotone" dataKey="totalVariance" stroke="hsl(0,72%,55%)" strokeWidth={2} dot={{ fill: 'hsl(0,72%,55%)', r: 3 }} name="Total Variance" />
              <Line type="monotone" dataKey="materialVariance" stroke="hsl(204,90%,45%)" strokeWidth={1} name="Material" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="bg-card border border-border rounded-lg p-4">
        <h3 className="text-sm font-semibold text-foreground mb-3">Recent Cost Rollups</h3>
        <DataTable columns={[
          { header: 'Date', cell: (r) => formatDate(r.buildDate) },
          { header: 'Material', cell: (r) => formatCurrency(r.materialCost) },
          { header: 'Labor', cell: (r) => formatCurrency(r.laborCost) },
          { header: 'Overhead', cell: (r) => formatCurrency(r.overheadCost) },
          { header: 'Total', cell: (r) => <span className="font-semibold">{formatCurrency(r.totalCost)}</span> },
          { header: 'Variance', cell: (r) => <span className={r.totalVariance > 0 ? 'text-[hsl(0,72%,55%)]' : 'text-[hsl(160,60%,55%)]'}>{formatCurrency(r.totalVariance)}</span> },
        ]} data={summary.recentRollups || []} />
      </div>

      {/* AI Cost Anomaly Results */}
      {costAnomaly && (
        <div className="bg-card border border-[hsl(var(--accent))]/30 rounded-lg p-4 mt-4">
          <h3 className="text-sm font-semibold text-[hsl(var(--accent))] mb-2">AI Cost Anomaly Analysis</h3>
          <div className="grid grid-cols-3 gap-3 mb-3 text-xs">
            <div><span className="text-muted-foreground">Avg Cost:</span> <span className="text-foreground font-semibold">{formatCurrency(costAnomaly.statistics?.averageCost)}</span></div>
            <div><span className="text-muted-foreground">Std Dev:</span> <span className="text-foreground">{formatCurrency(costAnomaly.statistics?.stdDev)}</span></div>
            <div><span className="text-muted-foreground">Anomalies:</span> <span className="text-foreground font-semibold">{costAnomaly.anomalies?.length || 0}</span></div>
          </div>
          {costAnomaly.aiHypothesis && <p className="text-sm text-foreground whitespace-pre-wrap">{costAnomaly.aiHypothesis}</p>}
        </div>
      )}
      </div>)}

      {/* Standard Costs Tab */}
      {tab === 'stdcosts' && (
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-3">Standard Cost Versions</h3>
          <DataTable columns={[
            { header: 'Part', cell: (r) => <span className="font-mono text-[hsl(var(--accent))]">{r.itemData?.partNumber || r.itemId?.slice(0, 8)}</span> },
            { header: 'Description', cell: (r) => r.itemData?.description || '—' },
            { header: 'Version', accessor: 'version' },
            { header: 'Material', cell: (r) => formatCurrency(r.materialCost) },
            { header: 'Labor', cell: (r) => formatCurrency(r.laborCost) },
            { header: 'Overhead', cell: (r) => formatCurrency(r.overheadCost) },
            { header: 'Total', cell: (r) => <span className="font-semibold">{formatCurrency(r.totalCost)}</span> },
            { header: 'Effective', cell: (r) => formatDate(r.effectiveDate) },
            { header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
            { header: '', cell: (r) => r.status === 'Draft' ? (
              <Button size="sm" onClick={() => { setSelectedSC(r); setSigOpen(true); }}>Approve</Button>
            ) : '' },
          ]} data={stdCosts} />
        </div>
      )}

      <ESignatureModal open={sigOpen} onClose={() => setSigOpen(false)} entityType="standard_cost" entityId={selectedSC?.id || ''}
        meaning="Approved" onSign={async (sig) => {
          if (selectedSC) { await costingAPI.approveStandardCost(selectedSC.id, { signatureId: sig.id }); toast.success('Standard cost approved'); costingAPI.standardCosts({}).then(r => setStdCosts(r.data)); }
        }} />
    </div>
  );
};

// ============ QUALITY ============
const QualityDashboard = () => {
  const [ncs, setNCs] = useState([]);
  const [capas, setCAPAs] = useState([]);
  const [ncTotal, setNCTotal] = useState(0);
  const [tab, setTab] = useState('nc');
  const [showCreateNC, setShowCreateNC] = useState(false);
  const [showCreateCAPA, setShowCreateCAPA] = useState(false);
  const [ncCorrelation, setNcCorrelation] = useState(null);
  const [newNC, setNewNC] = useState({ title: '', description: '', severity: 'Minor', sourceType: 'Receiving' });
  const [newCAPA, setNewCAPA] = useState({ title: '', description: '', type: 'Corrective', correctiveAction: '', preventiveAction: '' });
  const navigate = useNavigate();

  const load = () => {
    qualityAPI.ncRecords({}).then(r => { setNCs(r.data.items); setNCTotal(r.data.total); });
    qualityAPI.capaRecords({}).then(r => setCAPAs(r.data.items));
  };
  useEffect(() => { load(); }, []);

  const handleCreateNC = async () => {
    try {
      await qualityAPI.createNc(newNC);
      toast.success('NC record created');
      setShowCreateNC(false);
      setNewNC({ title: '', description: '', severity: 'Minor', sourceType: 'Receiving' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleCreateCAPA = async () => {
    try {
      await qualityAPI.createCapa(newCAPA);
      toast.success('CAPA record created');
      setShowCreateCAPA(false);
      setNewCAPA({ title: '', description: '', type: 'Corrective', correctiveAction: '', preventiveAction: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const runCorrelation = async () => {
    try {
      const res = await aiAPI.ncCorrelation({});
      setNcCorrelation(res.data);
      toast.success('NC correlation analysis complete');
    } catch (err) { toast.error('Analysis failed'); }
  };

  return (
    <div data-testid="quality-page">
      <PageHeader title="Quality Management" subtitle="NC/CAPA Tracking"
        actions={<div className="flex gap-2">
          <Button variant="outline" onClick={runCorrelation}>AI NC Analysis</Button>
          <Button onClick={() => tab === 'nc' ? setShowCreateNC(true) : setShowCreateCAPA(true)}>+ New {tab === 'nc' ? 'NC' : 'CAPA'}</Button>
        </div>} />
      <div className="grid grid-cols-4 gap-4 mb-6">
        <KPICard title="Open NCs" value={ncs.filter(n => n.status === 'Open').length} icon="⚠️" />
        <KPICard title="Critical NCs" value={ncs.filter(n => n.severity === 'Critical').length} icon="🔴" />
        <KPICard title="Open CAPAs" value={capas.filter(c => c.status === 'Open').length} icon="📋" />
        <KPICard title="Total NCs" value={ncTotal} icon="📊" />
      </div>

      {/* AI NC Correlation Results */}
      {ncCorrelation && (
        <div className="bg-card border border-[hsl(var(--accent))]/30 rounded-lg p-4 mb-4">
          <h3 className="text-sm font-semibold text-[hsl(var(--accent))] mb-2">AI NC Correlation Analysis</h3>
          <p className="text-sm text-foreground whitespace-pre-wrap mb-3">{ncCorrelation.aiAnalysis}</p>
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div>
              <h4 className="font-medium text-muted-foreground mb-1">By Supplier</h4>
              {ncCorrelation.supplierTrends?.map(s => (
                <div key={s.supplierId} className="flex justify-between py-0.5"><span>{s.supplierName}</span><span className="font-semibold">{s.ncCount} NCs</span></div>
              ))}
            </div>
            <div>
              <h4 className="font-medium text-muted-foreground mb-1">By Source</h4>
              {ncCorrelation.sourceTrends?.map(s => (
                <div key={s.source} className="flex justify-between py-0.5"><span>{s.source}</span><span className="font-semibold">{s.count}</span></div>
              ))}
            </div>
          </div>
        </div>
      )}

      <TabBar tabs={[{ id: 'nc', label: 'Nonconformances' }, { id: 'capa', label: 'CAPAs' }, { id: 'complaints', label: 'Complaints' }]} active={tab} onTabChange={setTab} />

      {/* Create NC Modal */}
      <Modal open={showCreateNC} onClose={() => setShowCreateNC(false)} title="Create Nonconformance" size="md">
        <div className="space-y-3">
          <div><label className="text-xs text-muted-foreground">Title</label>
            <input type="text" value={newNC.title} onChange={(e) => setNewNC(p => ({...p, title: e.target.value}))}
              className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground" /></div>
          <div><label className="text-xs text-muted-foreground">Description</label>
            <textarea value={newNC.description} onChange={(e) => setNewNC(p => ({...p, description: e.target.value}))}
              className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground" rows={3} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-muted-foreground">Severity</label>
              <select value={newNC.severity} onChange={(e) => setNewNC(p => ({...p, severity: e.target.value}))}
                className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground">
                <option value="Minor">Minor</option><option value="Major">Major</option><option value="Critical">Critical</option>
              </select></div>
            <div><label className="text-xs text-muted-foreground">Source</label>
              <select value={newNC.sourceType} onChange={(e) => setNewNC(p => ({...p, sourceType: e.target.value}))}
                className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground">
                <option value="Receiving">Receiving</option><option value="Production">Production</option><option value="Field">Field</option><option value="WorkOrder">Work Order</option>
              </select></div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowCreateNC(false)}>Cancel</Button>
            <Button onClick={handleCreateNC} disabled={!newNC.title}>Create NC</Button>
          </div>
        </div>
      </Modal>

      {/* Create CAPA Modal */}
      <Modal open={showCreateCAPA} onClose={() => setShowCreateCAPA(false)} title="Create CAPA" size="md">
        <div className="space-y-3">
          <div><label className="text-xs text-muted-foreground">Title</label>
            <input type="text" value={newCAPA.title} onChange={(e) => setNewCAPA(p => ({...p, title: e.target.value}))}
              className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground" /></div>
          <div><label className="text-xs text-muted-foreground">Description</label>
            <textarea value={newCAPA.description} onChange={(e) => setNewCAPA(p => ({...p, description: e.target.value}))}
              className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground" rows={2} /></div>
          <div><label className="text-xs text-muted-foreground">Type</label>
            <select value={newCAPA.type} onChange={(e) => setNewCAPA(p => ({...p, type: e.target.value}))}
              className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground">
              <option value="Corrective">Corrective</option><option value="Preventive">Preventive</option><option value="Both">Both</option>
            </select></div>
          <div><label className="text-xs text-muted-foreground">Corrective Action</label>
            <textarea value={newCAPA.correctiveAction} onChange={(e) => setNewCAPA(p => ({...p, correctiveAction: e.target.value}))}
              className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground" rows={2} /></div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowCreateCAPA(false)}>Cancel</Button>
            <Button onClick={handleCreateCAPA} disabled={!newCAPA.title}>Create CAPA</Button>
          </div>
        </div>
      </Modal>

      {tab === 'nc' && (
        <DataTable columns={[
          { header: 'NC Number', cell: (r) => <span className="font-mono text-[hsl(var(--accent))]">{r.ncNumber}</span> },
          { header: 'Title', accessor: 'title' },
          { header: 'Severity', cell: (r) => <StatusBadge status={r.severity} /> },
          { header: 'Source', accessor: 'sourceType' },
          { header: 'CAPA Required', cell: (r) => r.capaRequired ? 'Yes' : 'No' },
          { header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
          { header: 'Created', cell: (r) => formatDate(r.createdAt) },
        ]} data={ncs} onRowClick={(r) => navigate(`/quality/nc/${r.id}`)} />
      )}
      {tab === 'capa' && (
        <DataTable columns={[
          { header: 'CAPA Number', cell: (r) => <span className="font-mono text-[hsl(var(--accent))]">{r.capaNumber}</span> },
          { header: 'Title', accessor: 'title' },
          { header: 'Type', cell: (r) => <StatusBadge status={r.type} /> },
          { header: 'Due Date', cell: (r) => formatDate(r.dueDate) },
          { header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
        ]} data={capas} />
      )}
      {tab === 'complaints' && (
        <div>
          <ComplaintsSection />
        </div>
      )}
    </div>
  );
};

const ComplaintsSection = () => {
  const [complaints, setComplaints] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newComplaint, setNewComplaint] = useState({ title: '', description: '', severity: 'Minor', source: 'Customer', customerName: '' });

  useEffect(() => { qualityAPI.complaints({}).then(r => setComplaints(r.data.items)).catch(() => {}); }, []);

  const handleCreate = async () => {
    try {
      const res = await qualityAPI.createComplaint(newComplaint);
      toast.success(`Complaint created${res.data.linkedNcId ? ' (NC auto-created)' : ''}`);
      setShowCreate(false);
      setNewComplaint({ title: '', description: '', severity: 'Minor', source: 'Customer', customerName: '' });
      qualityAPI.complaints({}).then(r => setComplaints(r.data.items));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div>
      <div className="flex justify-end mb-3"><Button size="sm" onClick={() => setShowCreate(true)}>+ New Complaint</Button></div>
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Log Customer Complaint" size="md">
        <div className="space-y-3">
          <div><label className="text-xs text-muted-foreground">Title</label>
            <input type="text" value={newComplaint.title} onChange={(e) => setNewComplaint(p => ({...p, title: e.target.value}))}
              className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground" /></div>
          <div><label className="text-xs text-muted-foreground">Description</label>
            <textarea value={newComplaint.description} onChange={(e) => setNewComplaint(p => ({...p, description: e.target.value}))}
              className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground" rows={3} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-muted-foreground">Severity</label>
              <select value={newComplaint.severity} onChange={(e) => setNewComplaint(p => ({...p, severity: e.target.value}))}
                className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground">
                <option value="Minor">Minor</option><option value="Major">Major</option><option value="Critical">Critical</option>
              </select></div>
            <div><label className="text-xs text-muted-foreground">Customer</label>
              <input type="text" value={newComplaint.customerName} onChange={(e) => setNewComplaint(p => ({...p, customerName: e.target.value}))}
                className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground" /></div>
          </div>
          <div className="text-xs text-muted-foreground bg-secondary/30 p-2 rounded">Major/Critical complaints auto-create NC records</div>
          <div className="flex gap-2 justify-end"><Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button><Button onClick={handleCreate} disabled={!newComplaint.title}>Submit</Button></div>
        </div>
      </Modal>
      <DataTable columns={[
        { header: 'Number', cell: (r) => <span className="font-mono text-[hsl(var(--accent))]">{r.complaintNumber}</span> },
        { header: 'Title', accessor: 'title' },
        { header: 'Severity', cell: (r) => <StatusBadge status={r.severity} /> },
        { header: 'Source', accessor: 'source' },
        { header: 'Customer', accessor: 'customerName' },
        { header: 'Linked NC', cell: (r) => r.linkedNcId ? 'Yes' : 'No' },
        { header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
        { header: 'Created', cell: (r) => formatDate(r.createdAt) },
      ]} data={complaints} />
    </div>
  );
};

const NCDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [sigOpen, setSigOpen] = useState(false);
  const navigate = useNavigate();
  const load = () => qualityAPI.getNc(id).then(r => setData(r.data));
  useEffect(() => { load(); }, [id]);
  if (!data) return <div className="text-muted-foreground p-8">Loading...</div>;
  const nc = data.ncRecord;

  return (
    <div data-testid="nc-detail-page">
      <PageHeader title={nc.ncNumber} subtitle={nc.title}
        actions={<div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/quality')}>Back</Button>
          {nc.status === 'Open' && <Button onClick={() => setSigOpen(true)}>Disposition (e-Sign)</Button>}
        </div>} />
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div><span className="text-xs text-muted-foreground">Status</span><div><StatusBadge status={nc.status} /></div></div>
          <div><span className="text-xs text-muted-foreground">Severity</span><div><StatusBadge status={nc.severity} /></div></div>
          <div><span className="text-xs text-muted-foreground">Source</span><div className="text-foreground">{nc.sourceType}</div></div>
          <div className="col-span-3"><span className="text-xs text-muted-foreground">Description</span><div className="text-foreground mt-1">{nc.description}</div></div>
          {nc.rootCause && <div className="col-span-3"><span className="text-xs text-muted-foreground">Root Cause</span><div className="text-foreground mt-1">{nc.rootCause}</div></div>}
        </div>
      </div>
      <ESignatureModal open={sigOpen} onClose={() => setSigOpen(false)} entityType="nc_record" entityId={id}
        meaning="Approved" onSign={async (sig) => {
          await qualityAPI.dispositionNc(id, { disposition: 'Use As Is', signatureId: sig.id });
          toast.success('NC dispositioned'); load();
        }} />
    </div>
  );
};

// ============ EVOLVE INTERFACE ============
const EvolveInterface = () => {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  useEffect(() => {
    evolveAPI.status().then(r => setStatus(r.data));
    evolveAPI.interfaceLogs({}).then(r => setLogs(r.data));
  }, []);

  return (
    <div data-testid="evolve-page">
      <PageHeader title="Evolve Manufacturing Interface" subtitle="Controlled data exchange with Evolve QMS" />
      <div className="bg-secondary/60 border border-border rounded-lg p-3 mb-6 text-xs text-muted-foreground">
        DMR–DHR Boundary: This interface enforces strict data ownership. Inbound payloads containing DHR content are automatically rejected.
      </div>
      {status && (
        <div className="grid grid-cols-5 gap-4 mb-6">
          <KPICard title="Total Exchanges" value={status.total} icon="🔄" />
          <KPICard title="Sent" value={status.sent} icon="📤" />
          <KPICard title="Received" value={status.received} icon="📥" />
          <KPICard title="Failed" value={status.failed} icon="❌" />
          <KPICard title="Rejected (DHR)" value={status.rejected} icon="🛡️" />
        </div>
      )}
      <div className="bg-card border border-border rounded-lg p-4">
        <h3 className="text-sm font-semibold text-foreground mb-3">Interface Logs</h3>
        <DataTable columns={[
          { header: 'Direction', cell: (r) => <StatusBadge status={r.direction === 'OUTBOUND' ? 'Sent' : 'Received'} /> },
          { header: 'Data Type', accessor: 'dataType' },
          { header: 'Entity', accessor: 'entityType' },
          { header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
          { header: 'Sent', cell: (r) => formatDateTime(r.sentAt) },
          { header: 'Ack', cell: (r) => formatDateTime(r.acknowledgedAt) },
          { header: 'Failure', cell: (r) => r.failureReason || '—' },
        ]} data={logs} />
      </div>
    </div>
  );
};

// ============ AUDIT TRAIL ============
const AuditTrail = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [entityType, setEntityType] = useState('');
  const [action, setAction] = useState('');

  useEffect(() => {
    auditAPI.logs({ entity_type: entityType || undefined, action: action || undefined, limit: 100 })
      .then(r => { setLogs(r.data.items); setTotal(r.data.total); });
  }, [entityType, action]);

  return (
    <div data-testid="audit-page">
      <PageHeader title="Immutable Audit Trail" subtitle={`${total} log entries | 21 CFR Part 11 Compliant`}
        actions={<Button variant="outline" onClick={async () => {
          try {
            const res = await exportAPI.auditCsv({ entity_type: entityType || undefined, action: action || undefined });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a'); a.href = url; a.download = `audit_log_${new Date().toISOString().slice(0,10)}.csv`;
            document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
            toast.success('Audit log exported');
          } catch(e) { toast.error('Export failed'); }
        }}>Export CSV</Button>} />
      <div className="flex gap-3 mb-4">
        <select value={entityType} onChange={(e) => setEntityType(e.target.value)}
          className="bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground">
          <option value="">All Entity Types</option>
          {['user', 'session', 'inventory_item', 'bom', 'eco', 'work_order', 'purchase_order', 'nc_record', 'e_signature', 'evolve_interface'].map(t =>
            <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={action} onChange={(e) => setAction(e.target.value)}
          className="bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground">
          <option value="">All Actions</option>
          {['CREATE', 'UPDATE', 'DELETE', 'APPROVE', 'RELEASE', 'LOGIN', 'SIGN', 'TRANSFER', 'REJECT', 'CONVERT', 'ADVANCE_STAGE', 'DISPOSITION', 'RECEIVE'].map(a =>
            <option key={a} value={a}>{a}</option>)}
        </select>
      </div>
      <DataTable columns={[
        { header: 'Timestamp', cell: (r) => <span className="font-mono text-xs">{formatDateTime(r.timestamp)}</span> },
        { header: 'User', accessor: 'userEmail' },
        { header: 'Entity', cell: (r) => <span className="text-xs">{r.entityType}</span> },
        { header: 'Action', cell: (r) => <StatusBadge status={r.action} /> },
        { header: 'Field', accessor: 'fieldName' },
        { header: 'Old', cell: (r) => <span className="text-xs text-muted-foreground max-w-[100px] truncate block">{r.oldValue || '—'}</span> },
        { header: 'New', cell: (r) => <span className="text-xs text-foreground max-w-[100px] truncate block">{r.newValue || '—'}</span> },
        { header: 'Sig', cell: (r) => r.signatureId ? '✍️' : '' },
      ]} data={logs} />
    </div>
  );
};

// ============ USERS ============
const UsersPage = () => {
  const [users, setUsers] = useState([]);
  useEffect(() => { authAPI.users().then(r => setUsers(r.data)); }, []);

  return (
    <div data-testid="users-page">
      <PageHeader title="User Management" subtitle={`${users.length} users | RBAC Active`} />
      <DataTable columns={[
        { header: 'Name', cell: (r) => `${r.firstName} ${r.lastName}` },
        { header: 'Email', accessor: 'email' },
        { header: 'Role', cell: (r) => <StatusBadge status={r.role} /> },
        { header: 'Department', accessor: 'department' },
        { header: 'Title', accessor: 'title' },
        { header: 'Status', cell: (r) => <StatusBadge status={r.isActive ? 'Active' : 'Blocked'} /> },
        { header: 'Last Login', cell: (r) => formatDateTime(r.lastLogin) },
      ]} data={users} />
    </div>
  );
};

// ============ SUPPLIER PORTAL ============
const SupplierPortalLogin = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await portalAPI.login({ email });
      localStorage.setItem('portal_token', res.data.token);
      localStorage.setItem('portal_supplier', JSON.stringify(res.data.supplier));
      toast.success(`Welcome, ${res.data.supplier.name}`);
      navigate('/portal/purchase-orders');
    } catch (err) { toast.error('Invalid supplier email'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="bg-card border border-border rounded-lg p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-xl font-bold text-foreground">PROMAXO <span className="text-[hsl(var(--accent))]">Supplier Portal</span></h1>
          <p className="text-sm text-muted-foreground mt-2">Access your purchase orders and documents</p>
        </div>
        <form onSubmit={handleLogin} className="space-y-4">
          <div><label className="block text-sm text-foreground mb-1">Supplier Contact Email</label>
            <input data-testid="portal-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-secondary border border-border rounded-md px-3 py-2.5 text-sm text-foreground" placeholder="supplier@company.com" /></div>
          <Button data-testid="portal-login" type="submit" className="w-full" disabled={loading}>{loading ? 'Signing in...' : 'Sign In to Portal'}</Button>
        </form>
        <div className="mt-4 border-t border-border pt-3">
          <p className="text-xs text-muted-foreground mb-2 text-center">Demo Suppliers</p>
          <div className="grid grid-cols-2 gap-2">
            {[{name: 'MedTech', email: 'john@medtechcomp.com'}, {name: 'Precision Mag', email: 'lisa@precmag.com'}].map(s => (
              <button key={s.email} onClick={() => { setEmail(s.email); }} className="text-xs px-3 py-1.5 rounded border border-border text-muted-foreground hover:text-foreground hover:border-[hsl(var(--accent))]">{s.name}</button>
            ))}
          </div>
        </div>
        <div className="mt-4 text-center"><Link to="/login" className="text-xs text-muted-foreground hover:text-[hsl(var(--accent))]">Back to ERP Login</Link></div>
      </div>
    </div>
  );
};

const SupplierPortalLayout = ({ children }) => {
  const navigate = useNavigate();
  const supplier = JSON.parse(localStorage.getItem('portal_supplier') || '{}');
  return (
    <div className="min-h-screen bg-background">
      <header className="bg-card border-b border-border px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-sm font-bold text-foreground">PROMAXO <span className="text-[hsl(var(--accent))]">Portal</span></span>
          <nav className="flex gap-3 ml-6">
            {[{label: 'Purchase Orders', path: '/portal/purchase-orders'}, {label: 'ASNs', path: '/portal/asns'}, {label: 'Documents', path: '/portal/documents'}].map(n => (
              <Link key={n.path} to={n.path} className="text-sm text-muted-foreground hover:text-foreground">{n.label}</Link>
            ))}
          </nav>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">{supplier.name}</span>
          <button onClick={() => { localStorage.removeItem('portal_token'); localStorage.removeItem('portal_supplier'); navigate('/portal'); }}
            className="text-xs text-muted-foreground hover:text-destructive">Logout</button>
        </div>
      </header>
      <main className="p-6 max-w-7xl mx-auto">{children}</main>
    </div>
  );
};

const PortalPOs = () => {
  const [pos, setPOs] = useState([]);
  useEffect(() => { portalAPI.purchaseOrders().then(r => setPOs(r.data)).catch(() => {}); }, []);

  const handleAcknowledge = async (poId) => {
    try { await portalAPI.acknowledgePO(poId); toast.success('PO acknowledged'); portalAPI.purchaseOrders().then(r => setPOs(r.data)); }
    catch (err) { toast.error('Acknowledge failed'); }
  };

  return (
    <SupplierPortalLayout>
      <PageHeader title="Purchase Orders" subtitle={`${pos.length} orders assigned to you`} />
      <DataTable columns={[
        { header: 'PO Number', cell: (r) => <span className="font-mono text-[hsl(var(--accent))]">{r.poNumber}</span> },
        { header: 'Total', cell: (r) => formatCurrency(r.totalAmount) },
        { header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
        { header: 'Acknowledged', cell: (r) => r.acknowledgedAt ? formatDateTime(r.acknowledgedAt) : <Button size="sm" onClick={() => handleAcknowledge(r.id)}>Acknowledge</Button> },
        { header: 'Lines', cell: (r) => `${r.lines?.length || 0} items` },
        { header: 'Created', cell: (r) => formatDate(r.createdAt) },
      ]} data={pos} />
    </SupplierPortalLayout>
  );
};

const PortalASNs = () => {
  const [asns, setASNs] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ asnNumber: '', carrier: '', trackingNumber: '' });
  useEffect(() => { portalAPI.listASNs().then(r => setASNs(r.data)).catch(() => {}); }, []);

  return (
    <SupplierPortalLayout>
      <PageHeader title="Advanced Shipment Notices" subtitle={`${asns.length} ASNs`}
        actions={<Button onClick={() => setShowCreate(true)}>+ Submit ASN</Button>} />
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Submit ASN" size="md">
        <div className="space-y-3">
          <div><label className="text-xs text-muted-foreground">ASN Number</label>
            <input type="text" value={form.asnNumber} onChange={(e) => setForm(p => ({...p, asnNumber: e.target.value}))}
              className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-muted-foreground">Carrier</label>
              <input type="text" value={form.carrier} onChange={(e) => setForm(p => ({...p, carrier: e.target.value}))}
                className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground" /></div>
            <div><label className="text-xs text-muted-foreground">Tracking #</label>
              <input type="text" value={form.trackingNumber} onChange={(e) => setForm(p => ({...p, trackingNumber: e.target.value}))}
                className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground" /></div>
          </div>
          <div className="flex gap-2 justify-end"><Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button><Button>Submit</Button></div>
        </div>
      </Modal>
      <DataTable columns={[
        { header: 'ASN Number', cell: (r) => <span className="font-mono text-[hsl(var(--accent))]">{r.asnNumber}</span> },
        { header: 'Carrier', accessor: 'carrier' },
        { header: 'Tracking', accessor: 'trackingNumber' },
        { header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
        { header: 'Submitted', cell: (r) => formatDateTime(r.createdAt) },
      ]} data={asns} />
    </SupplierPortalLayout>
  );
};

const PortalDocuments = () => {
  const [docs, setDocs] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [form, setForm] = useState({ title: '', documentType: 'Certificate', certType: 'ISO 13485', expiryDate: '' });
  useEffect(() => { portalAPI.listDocuments().then(r => setDocs(r.data)).catch(() => {}); }, []);

  const handleUpload = async () => {
    try {
      await portalAPI.uploadDocument(form);
      toast.success('Document uploaded');
      setShowUpload(false);
      setForm({ title: '', documentType: 'Certificate', certType: 'ISO 13485', expiryDate: '' });
      portalAPI.listDocuments().then(r => setDocs(r.data));
    } catch (err) { toast.error('Upload failed'); }
  };

  return (
    <SupplierPortalLayout>
      <PageHeader title="Documents & Certifications" subtitle="Upload compliance documentation"
        actions={<Button onClick={() => setShowUpload(true)}>+ Upload Document</Button>} />
      <Modal open={showUpload} onClose={() => setShowUpload(false)} title="Upload Document" size="md">
        <div className="space-y-3">
          <div><label className="text-xs text-muted-foreground">Document Title</label>
            <input type="text" value={form.title} onChange={(e) => setForm(p => ({...p, title: e.target.value}))}
              className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground" placeholder="e.g. ISO 13485:2016 Certificate" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-muted-foreground">Document Type</label>
              <select value={form.documentType} onChange={(e) => setForm(p => ({...p, documentType: e.target.value}))}
                className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground">
                <option value="Certificate">Certificate</option><option value="CoC">Certificate of Conformance</option><option value="NDA">NDA</option><option value="AuditReport">Audit Report</option><option value="Other">Other</option>
              </select></div>
            <div><label className="text-xs text-muted-foreground">Certification Type</label>
              <select value={form.certType} onChange={(e) => setForm(p => ({...p, certType: e.target.value}))}
                className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground">
                <option value="ISO 13485">ISO 13485</option><option value="ISO 9001">ISO 9001</option><option value="CE Mark">CE Mark</option><option value="FDA Registration">FDA Registration</option><option value="Other">Other</option>
              </select></div>
          </div>
          <div><label className="text-xs text-muted-foreground">Expiry Date</label>
            <input type="date" value={form.expiryDate} onChange={(e) => setForm(p => ({...p, expiryDate: e.target.value}))}
              className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground" /></div>
          <div className="flex gap-2 justify-end"><Button variant="outline" onClick={() => setShowUpload(false)}>Cancel</Button><Button onClick={handleUpload} disabled={!form.title}>Upload</Button></div>
        </div>
      </Modal>
      <DataTable columns={[
        { header: 'Title', accessor: 'title' },
        { header: 'Type', cell: (r) => <StatusBadge status={r.documentType} /> },
        { header: 'Cert Type', accessor: 'certType' },
        { header: 'Expiry', cell: (r) => formatDate(r.expiryDate) },
        { header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
        { header: 'Uploaded', cell: (r) => formatDateTime(r.createdAt) },
      ]} data={docs} />
    </SupplierPortalLayout>
  );
};

// ============ VALIDATION BUNDLE ============
const ValidationPage = () => {
  const [bundles, setBundles] = useState([]);
  const [currentBundle, setCurrentBundle] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { validationAPI.listBundles().then(r => setBundles(r.data)).catch(() => {}); }, []);

  const generateBundle = async () => {
    setLoading(true);
    try {
      const res = await validationAPI.generateBundle();
      setCurrentBundle(res.data);
      toast.success(`Validation bundle generated: ${res.data.overallStatus}`);
      validationAPI.listBundles().then(r => setBundles(r.data));
    } catch (err) { toast.error('Bundle generation failed'); }
    finally { setLoading(false); }
  };

  const downloadPdf = async (type) => {
    try {
      let res;
      if (type === 'compliance') res = await exportAPI.compliancePdf();
      else if (type === 'traceability') res = await exportAPI.traceabilityPdf();
      else if (type === 'audit') res = await exportAPI.auditPdf({});
      else if (type === 'daily') res = await exportAPI.dailySummaryPdf();
      else if (type === 'bundle' && currentBundle) res = await validationAPI.downloadBundlePdf(currentBundle.id);
      if (res) { downloadBlob(new Blob([res.data]), `${type}_report_${new Date().toISOString().slice(0,10)}.pdf`); toast.success('PDF downloaded'); }
    } catch (err) { toast.error('Download failed'); }
  };

  return (
    <div data-testid="validation-page">
      <PageHeader title="IQ/OQ/PQ Validation & Evidence" subtitle="FDA GAMP 5 Compliance Packaging"
        actions={<Button onClick={generateBundle} disabled={loading}>{loading ? 'Generating...' : 'Generate IQ/OQ/PQ Bundle'}</Button>} />

      {/* PDF Downloads */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { label: 'Compliance PDF', type: 'compliance', icon: '🛡️' },
          { label: 'Traceability PDF', type: 'traceability', icon: '📋' },
          { label: 'Audit Trail PDF', type: 'audit', icon: '📝' },
          { label: 'Daily Summary PDF', type: 'daily', icon: '📊' },
          { label: 'IQ/OQ/PQ PDF', type: 'bundle', icon: '✅' },
        ].map(btn => (
          <button key={btn.type} onClick={() => downloadPdf(btn.type)}
            className="bg-card border border-border rounded-lg p-4 text-center hover:border-[hsl(var(--accent))] transition-colors">
            <div className="text-2xl mb-2">{btn.icon}</div>
            <div className="text-xs font-medium text-foreground">{btn.label}</div>
            <div className="text-[10px] text-muted-foreground mt-1">Download</div>
          </button>
        ))}
      </div>

      {/* Current Bundle Results */}
      {currentBundle && (
        <div className="mb-6">
          <div className="grid grid-cols-4 gap-4 mb-4">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="text-xs text-muted-foreground">Overall Status</div>
              <div className="text-xl font-bold text-[hsl(160,60%,55%)]">{currentBundle.overallStatus}</div>
            </div>
            <KPICard title="IQ Checks" value={`${currentBundle.iq?.passCount}/${currentBundle.iq?.totalChecks}`} icon="🔧" />
            <KPICard title="OQ Checks" value={`${currentBundle.oq?.passCount}/${currentBundle.oq?.totalChecks}`} icon="⚙️" />
            <KPICard title="PQ Checks" value={`${currentBundle.pq?.passCount}/${currentBundle.pq?.totalChecks}`} icon="📈" />
          </div>

          {/* IQ Results */}
          <div className="bg-card border border-border rounded-lg p-4 mb-3">
            <h3 className="text-sm font-semibold text-foreground mb-2">Installation Qualification ({currentBundle.iq?.passCount}/{currentBundle.iq?.totalChecks})</h3>
            <DataTable columns={[
              { header: 'Check Item', accessor: 'item' },
              { header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
              { header: 'Detail', accessor: 'detail' },
            ]} data={currentBundle.iq?.checks || []} />
          </div>

          {/* OQ Results */}
          <div className="bg-card border border-border rounded-lg p-4 mb-3">
            <h3 className="text-sm font-semibold text-foreground mb-2">Operational Qualification ({currentBundle.oq?.passCount}/{currentBundle.oq?.totalChecks})</h3>
            <DataTable columns={[
              { header: 'Test Item', accessor: 'item' },
              { header: 'Result', cell: (r) => <StatusBadge status={r.status} /> },
              { header: 'Evidence', accessor: 'detail' },
            ]} data={currentBundle.oq?.checks || []} />
          </div>

          {/* PQ Results */}
          <div className="bg-card border border-border rounded-lg p-4 mb-3">
            <h3 className="text-sm font-semibold text-foreground mb-2">Performance Qualification ({currentBundle.pq?.passCount}/{currentBundle.pq?.totalChecks})</h3>
            <DataTable columns={[
              { header: 'Performance Item', accessor: 'item' },
              { header: 'Result', cell: (r) => <StatusBadge status={r.status} /> },
              { header: 'Evidence', accessor: 'detail' },
            ]} data={currentBundle.pq?.checks || []} />
          </div>
        </div>
      )}

      {/* Previous Bundles */}
      {bundles.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Previous Validation Bundles</h3>
          <DataTable columns={[
            { header: 'Bundle ID', cell: (r) => <span className="font-mono text-xs">{r.id?.slice(0, 8)}</span> },
            { header: 'Status', cell: (r) => <StatusBadge status={r.overallStatus} /> },
            { header: 'Generated', cell: (r) => formatDateTime(r.generatedAt) },
            { header: 'By', accessor: 'generatedBy' },
          ]} data={bundles} />
        </div>
      )}
    </div>
  );
};

// ============ GO-LIVE READINESS ============
const ReadinessPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const res = await readinessAPI.checklist(); setData(res.data); }
    catch (err) { toast.error('Failed to load checklist'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-muted-foreground p-8">{loading ? 'Loading...' : 'Click Refresh to load checklist'}</div>;

  const categories = [...new Set(data.checklist.map(c => c.category))];
  const statusIcon = { Pass: '✅', Warning: '⚠️', Fail: '❌' };
  const readinessColor = { Ready: 'text-[hsl(160,60%,55%)]', Conditional: 'text-[hsl(43,90%,55%)]', 'Not Ready': 'text-[hsl(0,72%,55%)]' };

  return (
    <div data-testid="readiness-page">
      <PageHeader title="Go-Live Readiness" subtitle="FDA/ISO compliance assessment"
        actions={<Button onClick={load} disabled={loading}>{loading ? 'Assessing...' : 'Refresh Assessment'}</Button>} />

      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-card border border-border rounded-lg p-4 col-span-1">
          <div className="text-xs text-muted-foreground uppercase mb-1">Readiness Status</div>
          <div className={`text-2xl font-bold ${readinessColor[data.readiness] || ''}`}>{data.readiness}</div>
        </div>
        <KPICard title="Pass" value={data.summary.pass} icon="✅" />
        <KPICard title="Warnings" value={data.summary.warning} icon="⚠️" />
        <KPICard title="Failures" value={data.summary.fail} icon="❌" />
      </div>

      {categories.map(cat => (
        <div key={cat} className="mb-4">
          <h3 className="text-sm font-semibold text-foreground mb-2">{cat}</h3>
          <div className="bg-card border border-border rounded-lg divide-y divide-border">
            {data.checklist.filter(c => c.category === cat).map((check, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="text-base">{statusIcon[check.status]}</span>
                  <div>
                    <div className="text-sm text-foreground">{check.item}</div>
                    <div className="text-xs text-muted-foreground">{check.detail}</div>
                  </div>
                </div>
                <StatusBadge status={check.status} />
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="text-xs text-muted-foreground mt-2">Assessed: {formatDateTime(data.assessedAt)}</div>
    </div>
  );
};

// ============ AI COPILOT PANEL ============
const AICopilot = ({ open, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(`chat-${Date.now()}`);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = { role: 'user', content: input, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    try {
      const res = await aiAPI.chat({ message: input, sessionId });
      const aiMsg = {
        role: 'assistant', content: res.data.response,
        timestamp: new Date().toISOString(), sources: res.data.sources,
        guardrails: res.data.guardrails, confidence: res.data.confidence
      };
      setMessages(prev => [...prev, aiMsg]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. The ERP continues to function normally.', timestamp: new Date().toISOString() }]);
    } finally { setLoading(false); }
  };

  if (!open) return null;
  return (
    <div data-testid="ai-copilot" className="fixed right-0 top-0 h-screen w-96 bg-card border-l border-border z-50 flex flex-col animate-slide-in">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div>
          <h3 className="text-sm font-semibold text-foreground">AI Copilot</h3>
          <p className="text-xs text-muted-foreground">Ask about inventory, BOMs, orders, quality...</p>
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-xl">&times;</button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <div className="text-3xl mb-3">🤖</div>
            <p className="text-sm text-muted-foreground">I'm your ERP AI Copilot. Ask me about:</p>
            <div className="mt-3 space-y-1">
              {['What parts are low on stock?', 'Show me open work orders', 'Any critical quality issues?', 'What is our cost variance trend?'].map(q => (
                <button key={q} onClick={() => { setInput(q); }} className="block w-full text-left text-xs text-[hsl(var(--accent))] hover:bg-secondary/60 px-3 py-1.5 rounded-md">{q}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${msg.role === 'user' ? 'bg-[hsl(var(--accent))] text-white' : 'bg-secondary/60 text-foreground'}`}>
              <p className="whitespace-pre-wrap">{msg.content}</p>
              {msg.sources?.length > 0 && (
                <div className="mt-2 flex gap-1 flex-wrap">
                  {msg.sources.map((s, j) => (
                    <span key={j} className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{s.type}: {s.count}</span>
                  ))}
                </div>
              )}
              {msg.guardrails?.dhr_boundary === 'blocked' && (
                <div className="mt-1 text-[10px] text-[hsl(0,72%,70%)]">DHR boundary enforced</div>
              )}
            </div>
          </div>
        ))}
        {loading && <div className="flex justify-start"><div className="bg-secondary/60 rounded-lg px-3 py-2 text-sm text-muted-foreground">Thinking...</div></div>}
      </div>
      <div className="p-3 border-t border-border">
        <div className="flex gap-2">
          <input data-testid="ai-input" type="text" value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Ask about your ERP data..."
            className="flex-1 bg-secondary border border-border rounded-md px-3 py-2 text-sm text-foreground placeholder-muted-foreground" />
          <Button data-testid="ai-send" size="sm" onClick={sendMessage} disabled={loading}>Send</Button>
        </div>
        <div className="mt-2 text-[10px] text-muted-foreground text-center">Compliant with 21 CFR Part 11 | DHR boundary enforced</div>
      </div>
    </div>
  );
};

// ============ COMPLIANCE SCAN ============
const ComplianceScanPage = () => {
  const [scan, setScan] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { aiAPI.agentStatus().then(r => setAgents(r.data.agents)).catch(() => {}); }, []);

  const runScan = async () => {
    setLoading(true);
    try {
      const res = await aiAPI.complianceScan();
      setScan(res.data);
      toast.success(`Compliance scan complete. Score: ${res.data.complianceScore}`);
    } catch (err) { toast.error('Scan failed'); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="compliance-page">
      <PageHeader title="AI Compliance & Agents" subtitle="Autonomous monitoring and compliance validation"
        actions={<Button onClick={runScan} disabled={loading}>{loading ? 'Scanning...' : 'Run Compliance Scan'}</Button>} />

      {/* Agent Status */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">Autonomous Agents (Tier 3)</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {agents.map(agent => (
            <div key={agent.id} className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-foreground">{agent.name}</h4>
                <StatusBadge status={agent.status} />
              </div>
              <p className="text-xs text-muted-foreground mb-3">{agent.description}</p>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{agent.actionsPerHour} actions/hr</span>
                <span>Limit: {agent.rateLimit}/hr</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Scan Results */}
      {scan && (
        <div>
          <div className="grid grid-cols-4 gap-4 mb-6">
            <KPICard title="Compliance Score" value={`${scan.complianceScore}%`}
              icon={scan.complianceScore >= 90 ? '🟢' : scan.complianceScore >= 70 ? '🟡' : '🔴'} />
            <KPICard title="Critical" value={scan.criticalFindings} icon="🔴" />
            <KPICard title="Major" value={scan.majorFindings} icon="🟠" />
            <KPICard title="Warnings" value={scan.warningFindings} icon="🟡" />
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Findings ({scan.totalFindings})</h3>
            <DataTable columns={[
              { header: 'Type', cell: (r) => <span className="font-mono text-xs">{r.type}</span> },
              { header: 'Severity', cell: (r) => <StatusBadge status={r.severity} /> },
              { header: 'Detail', accessor: 'detail' },
              { header: 'Recommendation', accessor: 'recommendation' },
            ]} data={scan.findings} />
          </div>
          <div className="mt-3 text-xs text-muted-foreground">
            Standards: {scan.standards?.join(' | ')} | Scanned: {formatDateTime(scan.scannedAt)}
          </div>
        </div>
      )}
    </div>
  );
};

// ============ LAYOUT ============
const AppLayout = ({ children }) => {
  const { collapsed } = useSidebarStore();
  const [copilotOpen, setCopilotOpen] = useState(false);
  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <main className={`transition-all duration-200 ${collapsed ? 'ml-16' : 'ml-56'} ${copilotOpen ? 'mr-96' : ''} p-6`}>{children}</main>
      {/* AI Copilot Toggle */}
      <button data-testid="ai-toggle" onClick={() => setCopilotOpen(!copilotOpen)}
        className="fixed bottom-20 right-6 z-[9999] bg-[hsl(var(--accent))] text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg hover:bg-[hsl(204,90%,40%)] transition-colors text-xl"
        style={{ pointerEvents: 'auto' }}>
        🤖
      </button>
      <AICopilot open={copilotOpen} onClose={() => setCopilotOpen(false)} />
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const { token } = useAuthStore();
  if (!token) return <Navigate to="/login" replace />;
  return <AppLayout>{children}</AppLayout>;
};

// ============ APP ============
function App() {
  useEffect(() => {
    // Auto-seed on first load
    seedAPI.seed().catch(() => {});
  }, []);

  return (
    <BrowserRouter>
      <Toaster theme="dark" position="top-right" richColors />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/inventory" element={<ProtectedRoute><InventoryList /></ProtectedRoute>} />
        <Route path="/inventory/:id" element={<ProtectedRoute><InventoryDetail /></ProtectedRoute>} />
        <Route path="/bom" element={<ProtectedRoute><BOMList /></ProtectedRoute>} />
        <Route path="/bom/:id" element={<ProtectedRoute><BOMDetail /></ProtectedRoute>} />
        <Route path="/eco" element={<ProtectedRoute><ECOList /></ProtectedRoute>} />
        <Route path="/eco/:id" element={<ProtectedRoute><ECODetail /></ProtectedRoute>} />
        <Route path="/work-orders" element={<ProtectedRoute><WorkOrderList /></ProtectedRoute>} />
        <Route path="/work-orders/:id" element={<ProtectedRoute><WorkOrderDetail /></ProtectedRoute>} />
        <Route path="/mrp" element={<ProtectedRoute><MRPDashboard /></ProtectedRoute>} />
        <Route path="/procurement" element={<ProtectedRoute><ProcurementPage /></ProtectedRoute>} />
        <Route path="/procurement/:id" element={<ProtectedRoute><PODetail /></ProtectedRoute>} />
        <Route path="/suppliers" element={<ProtectedRoute><SupplierList /></ProtectedRoute>} />
        <Route path="/suppliers/:id" element={<ProtectedRoute><SupplierDetail /></ProtectedRoute>} />
        <Route path="/costing" element={<ProtectedRoute><CostingDashboard /></ProtectedRoute>} />
        <Route path="/quality" element={<ProtectedRoute><QualityDashboard /></ProtectedRoute>} />
        <Route path="/quality/nc/:id" element={<ProtectedRoute><NCDetail /></ProtectedRoute>} />
        <Route path="/evolve" element={<ProtectedRoute><EvolveInterface /></ProtectedRoute>} />
        <Route path="/audit" element={<ProtectedRoute><AuditTrail /></ProtectedRoute>} />
        <Route path="/compliance" element={<ProtectedRoute><ComplianceScanPage /></ProtectedRoute>} />
        <Route path="/readiness" element={<ProtectedRoute><ReadinessPage /></ProtectedRoute>} />
        <Route path="/validation" element={<ProtectedRoute><ValidationPage /></ProtectedRoute>} />
        <Route path="/users" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
        {/* Supplier Portal Routes */}
        <Route path="/portal" element={<SupplierPortalLogin />} />
        <Route path="/portal/login" element={<SupplierPortalLogin />} />
        <Route path="/portal/purchase-orders" element={<PortalPOs />} />
        <Route path="/portal/asns" element={<PortalASNs />} />
        <Route path="/portal/documents" element={<PortalDocuments />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
